/**
 * TradeDrawer Component
 *
 * Trade creation/edit form drawer for the Live Trading cockpit.
 *
 * Surface:
 *   - Desktop: right-side Drawer (size="lg")
 *   - Mobile:  full-screen bottom Drawer (size="full")
 *
 * Layout (fixed header + tab nav → scroll body → fixed footer):
 *   Header:  close button (HeroUI) + title + editable trade name Input
 *             + tab navigation row (Setup / Docs / Execution / Review)
 *   Body:    Tab-gated scrollable content
 *             Setup:     Strategy & Playbook → Criteria → Confluence → Entry Models
 *             Docs:      NotesSection (T12 restructures to pre-entry/entry/management/exit phases)
 *             Execution: InstrumentDropdown + state-machine panel (T13; state from TradeExecution.executionStatus)
 *             Review:    QualitativeReviewSection (T15 restructures; null in draft mode)
 *             + Log as Missed inline expansion (when isLogAsMissedOpen)
 *   Footer (context-sensitive, T10 contract):
 *     New draft:                  Shelve | Log as Missed | Save Draft ✓
 *     Saved draft (clean):        Shelve/Unshelve | Log as Missed
 *     Saved draft (dirty):        Discard Changes | Shelve/Unshelve | Log as Missed | Save Draft ✓
 *     Persistent (pending/open):  Shelve/Unshelve | Mark Ready (disabled)
 *     Persistent (closed/cancel): Shelve/Unshelve | Mark Ready
 *     Persistent (ready-for-rev): Shelve/Unshelve | Complete ✓
 *     Persistent (complete):      (read-only)
 *     Log as Missed expanded:     Cancel expansion | Confirm ✓
 *
 * Tab smart defaults:
 *   - Draft mode (new or edit, not promoted) → Setup
 *   - Promoted, workflowStatus !== 'complete'  → Execution
 *   - Promoted, workflowStatus === 'complete'  → Review
 *
 * isDirty tracking (edit mode only):
 *   - Starts false on open, becomes true on any form field change
 *   - handleDiscardChanges resets form to existingDraft values and clears isDirty
 *
 * Form state is local and resets on drawer close.
 * Playbook selection auto-populates Criteria, Confluence, and Entry Models.
 * Trade name: auto-generates "{Strategy}:{Playbook} HH:MM" on playbook select;
 *             locks once manually edited; resets on close.
 *
 * draftId generated on open (new mode) or set to existingDraft.id (edit mode).
 * noteCategories loaded from LiveTradingSettingsService on mount.
 * drawerImages lifted from NotesSection via onImagesChange callback.
 *
 * WU2A: Handles TradeSetupDraft only. OpportunityDraft editing is WU03 scope.
 *       "Log as Missed" writes an OpportunityDraft (artifactKind: 'opportunity').
 *
 * @see TradesPane — manages isOpen/onClose state and handleCommit
 * @see CriteriaSection, ConfluenceSection, EntryModelsSection, NotesSection — form sections
 * @see mockTradingData — development-only reason data (strategy/playbook now uses real service data)
 * @see liveTradingSettingsService — source of noteCategories
 * @see TradeDrawer-Tab-Design.md — tab layout + footer contract spec (PR61 rework)
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useRef } from 'react';

// 2. Internal absolute imports
import {
  Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter,
  Input, Select, SelectItem,
  Button, Divider,
  Accordion, AccordionItem,
  Tabs, Tab,
  Popover, PopoverTrigger, PopoverContent,
} from '@enfomentis/ui';
import { Info } from 'lucide-react';
import {
  MOCK_REASON_CATEGORIES,
  getMockReasonsForCategory,
} from '@/constants/mockTradingData';
import { accountRiskService } from '@/services/AccountRiskService';
import { accountService } from '@/services/AccountService';
import { imageStorageService } from '@/services/ImageStorageService';
import { instrumentService } from '@/services/InstrumentService';
import { liveTradingSettingsService } from '@/services/LiveTradingSettingsService';
import { noteDraftService } from '@/services/NoteDraftService';
import { sessionDraftService } from '@/services/SessionDraftService';
import { tradeService } from '@/services/TradeService';
import { tradeExecutionService } from '@/services/TradeExecutionService';
import { tradeExecutionEventService } from '@/services/TradeExecutionEventService';
// tradePromotionService is called inside ExecutionEventModal (T14) — no import needed here
import type { Account } from '@/types/schema/Account';
import type { Confluence } from '@/types/schema/Confluence';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { Playbook } from '@/types/schema/Playbook';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { Strategy } from '@/types/schema/Strategy';
import type { Trade } from '@/types/schema/Trade';
import type { Instrument } from '@/types/schema/Instrument';
import type { TradeExecution } from '@/types/schema/TradeExecution';
import type { TradeExecutionEvent } from '@/types/schema/TradeExecutionEvent';
import type { InstrumentSnapshot } from '@/types/shared/InstrumentSnapshot';
import type { StrategySnapshot, PlaybookSnapshot } from '@/types/shared/StrategySnapshots';
import { checkPromotionReadiness, computeMaxOppositeOrderQty, getActiveOppositeOrderPrices, generateUUID, resolveRiskContextTier1 } from '@/utils';
import { computeEffectiveRisk, determineDerivationSource, getDefaultRiskProfile } from '@/utils/riskComputationUtils';
import { formatRisk } from '@/utils/riskDisplayHelpers';
import { useIsDesktop } from '@/hooks/useIsDesktop';

// 3. Type imports
import type { CriteriaSnapshot, ConfluenceSnapshot, EntryModelSnapshot } from '@/types/shared/StrategySnapshots';
import type { LiveTradingSettings, QuickTradePreset } from '@/types/schema/LiveTradingSettings';
import type { TradingSetupRiskSnapshot } from '@/types/schema/TradingSetupSnapshots';
import type { TradeSetupDraft, OpportunityDraft } from '@/types/transient/TradeDraft';
import type { ResolvedRiskContext } from '@/utils';

// 4. Relative imports
import { CriteriaSection } from './sections/CriteriaSection';
import { ConfluenceSection } from './sections/ConfluenceSection';
import { EntryModelsSection } from './sections/EntryModelsSection';
import { InstrumentDropdown } from '@/components/shared/InstrumentExplorer/InstrumentDropdown';
import { ExecutionEventModal } from './sections/ExecutionEventModal';
import { NotesSection } from './sections/NotesSection';
import { QualitativeReviewSection } from './sections/QualitativeReviewSection';
import type { QualitativeReviewSectionValue } from './sections/QualitativeReviewSection';

// ===== TYPES =====

// Fix 2: aligned to TradeSetupDraft['phase1']['images'] — includes 'order' phase
type DrawerImages = NonNullable<TradeSetupDraft['phase1']['images']>;

/** T10: Active tab in the drawer. Smart default: draft→setup, promoted→execution, complete→review */
type DrawerTab = 'setup' | 'docs' | 'execution' | 'review';

export interface TradeDrawerProps {
  /** Controls drawer visibility */
  isOpen: boolean;
  /** Called on close (X button, backdrop click, or Cancel CTA) */
  onClose: () => void;
  /** 'new' = creation mode; 'edit' = edit mode */
  mode?: 'new' | 'edit';
  /** Existing TradeSetupDraft to populate form in edit mode */
  existingDraft?: TradeSetupDraft;
  /** Called by all commit CTAs — TradesPane handles persistence */
  onCommit?: (draft: TradeSetupDraft | OpportunityDraft) => Promise<void>;
  /** Auto-saves the in-progress draft without closing — called on debounced form changes (new mode only) */
  onAutoSave?: (draft: TradeSetupDraft) => Promise<void>;
  /** Removes an auto-saved draft on cancel — called in new mode when drawer closes without commit */
  onCancelDraft?: (draftId: string) => Promise<void>;
  /** When true, open the Log as Missed expansion immediately on open (new mode) */
  logAsMissedPreOpen?: boolean;
  /** Quick Trade preset to pre-fill form on open (new mode only) */
  quickTradePreset?: QuickTradePreset;
  /**
   * Session draft ID — used to stamp new drafts with canonical ownership.
   * Optional: drafts built without it will have empty sessionDraftId (acceptable in WU2A).
   */
  sessionDraftId?: string;
  /**
   * Session-default account ID — used to auto-seed the trade's account on new draft creation.
   * The drawer owns `selectedAccountId` state; this prop is the initial default only.
   * Sourced from `SessionDraft.defaultAccountId` via LiveTradingPhase → TradesPane.
   */
  defaultAccountId?: string;
  /** Active strategies loaded by LiveTradingPhase — used for strategy select + snapshot building */
  activeStrategies?: Strategy[];
  /** Active playbooks loaded by LiveTradingPhase — used for playbook cascade + snapshot building */
  activePlaybooks?: Playbook[];
  /** Active criteria loaded by LiveTradingPhase — used for criteria snapshot building on playbook select */
  activeCriteria?: PlaybookCriteriaItem[];
  /** Active confluences loaded by LiveTradingPhase — used for confluence snapshot building on playbook select */
  activeConfluences?: Confluence[];
  /** Active entry models loaded by LiveTradingPhase — used for entry model snapshot building on playbook select */
  activeEntryModels?: EntryModel[];
}

// ===== CONSTANTS =====

// ===== HELPERS =====

const buildAutoName = (strategyName: string, playbookName: string): string => {
  const time = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return `${strategyName}:${playbookName} ${time}`;
};

const buildDefaultTradeName = (): string => {
  const time = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return `New Trade ${time}`;
};

// T13: Execution tab helpers — instrument snapshot capture + state-machine display labels

const buildInstrumentSnapshot = (instrument: Instrument): InstrumentSnapshot => ({
  instrumentId: instrument.id,
  symbol: instrument.symbol,
  displayName: instrument.displayName,
  assetClass: instrument.classification,
  contractSize: instrument.contractSize,
  contractAsset: instrument.contractAsset,
  incrementSize: instrument.incrementSize,
  incrementType: instrument.incrementType,
  quoteCurrency: instrument.quoteCurrency,
});

/** Build a StrategySnapshot from a real Strategy entity */
const buildStrategySnapshot = (strategy: Strategy): StrategySnapshot => ({
  strategyId: strategy.id,
  name: strategy.name,
  ...(strategy.description !== undefined ? { description: strategy.description } : {}),
  ...(strategy.philosophy !== undefined ? { philosophy: strategy.philosophy } : {}),
});

/** Build a PlaybookSnapshot from a real Playbook entity */
const buildPlaybookSnapshot = (playbook: Playbook): PlaybookSnapshot => ({
  playbookId: playbook.id,
  name: playbook.name,
  ...(playbook.description !== undefined ? { description: playbook.description } : {}),
  ...(playbook.philosophy !== undefined ? { philosophy: playbook.philosophy } : {}),
});

/** Build CriteriaSnapshots for a playbook from preloaded criteria */
const buildCriteriaSnapshots = (playbook: Playbook, allCriteria: PlaybookCriteriaItem[]): CriteriaSnapshot[] => {
  if (!playbook.criteriaIds || playbook.criteriaIds.length === 0) return [];
  return playbook.criteriaIds
    .map((id) => allCriteria.find((c) => c.id === id))
    .filter((c): c is PlaybookCriteriaItem => c !== undefined)
    .map((c) => ({
      criteriaId: c.id,
      name: c.name,
      ...(c.description !== undefined ? { description: c.description } : {}),
      checked: false,
    }));
};

/** Build ConfluenceSnapshots for a playbook from preloaded confluences — merges polarity from PlaybookConfluenceLink */
const buildConfluenceSnapshots = (playbook: Playbook, allConfluences: Confluence[]): ConfluenceSnapshot[] => {
  if (!playbook.confluences || playbook.confluences.length === 0) return [];
  return playbook.confluences
    .map((link) => {
      const confluence = allConfluences.find((c) => c.id === link.confluenceId);
      if (!confluence) return undefined;
      return {
        confluenceId: confluence.id,
        name: confluence.name,
        ...(confluence.description !== undefined ? { description: confluence.description } : {}),
        polarity: link.polarity,
        checked: false,
      };
    })
    .filter((c): c is ConfluenceSnapshot => c !== undefined);
};

/** Build EntryModelSnapshots for a playbook from preloaded entry models */
const buildEntryModelSnapshots = (playbook: Playbook, allEntryModels: EntryModel[]): EntryModelSnapshot[] => {
  return playbook.entryModelIds
    .map((id) => allEntryModels.find((em) => em.id === id))
    .filter((em): em is EntryModel => em !== undefined)
    .map((em) => ({
      entryModelId: em.id,
      name: em.name,
      ...(em.description !== undefined ? { description: em.description } : {}),
      checked: false,
    }));
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  open: 'Open',
  'partial-exit': 'Partial Exit',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const STATUS_CLASSES: Record<string, string> = {
  pending: 'text-warning',
  open: 'text-success',
  'partial-exit': 'text-warning',
  closed: 'text-text-secondary',
  cancelled: 'text-danger',
};

const EVENT_LABELS: Record<string, string> = {
  'order-placed': 'Order Placed',
  'order-amended': 'Order Amended',
  'order-cancelled': 'Order Cancelled',
  'entry-fill': 'Entry Fill',
  'partial-exit': 'Partial Exit',
  'final-exit': 'Final Exit',
  'stop-moved': 'Stop Moved',
  'take-profit-updated': 'TP Updated',
  'overnight-financing-applied': 'Overnight Fee',
  'currency-conversion-applied': 'FX Conversion',
};

const fmtTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch {
    return iso;
  }
};

// ===== COMPONENT =====

export const TradeDrawer: React.FC<TradeDrawerProps> = ({
  isOpen,
  onClose,
  mode = 'new',
  existingDraft,
  onCommit,
  onAutoSave,
  onCancelDraft,
  logAsMissedPreOpen = false,
  quickTradePreset,
  sessionDraftId,
  defaultAccountId,
  activeStrategies = [],
  activePlaybooks = [],
  activeCriteria = [],
  activeConfluences = [],
  activeEntryModels = [],
}) => {
  const isDesktop = useIsDesktop();

  // ---------------------------------------------------------------------------
  // Form state
  // ---------------------------------------------------------------------------

  const [draftId, setDraftId] = useState('');
  // Ref kept in sync so the isOpen effect can read the current draftId without
  // adding it to the dependency array (which would cause re-runs on open).
  const draftIdRef = useRef('');
  const [tradeName, setTradeName] = useState('');
  const [tradeNameManuallyEdited, setTradeNameManuallyEdited] = useState(false);
  const [strategyId, setStrategyId] = useState('');
  const [playbookId, setPlaybookId] = useState('');
  const [criteria, setCriteria] = useState<CriteriaSnapshot[]>([]);
  const [confluences, setConfluences] = useState<ConfluenceSnapshot[]>([]);
  const [entryModels, setEntryModels] = useState<EntryModelSnapshot[]>([]);

  // ---------------------------------------------------------------------------
  // Account context — drawer-owned; persisted via autosave
  // ---------------------------------------------------------------------------

  /** Account selected for this trade — autosaved to draft; auto-defaulted from session on new-mode open */
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // ---------------------------------------------------------------------------
  // Images — lifted from NotesSection via onImagesChange
  // ---------------------------------------------------------------------------

  const [drawerImages, setDrawerImages] = useState<DrawerImages>({});

  // ---------------------------------------------------------------------------
  // Log as Missed expansion state
  // ---------------------------------------------------------------------------

  const [isLogAsMissedOpen, setIsLogAsMissedOpen] = useState(false);
  const [reasonCategoryId, setReasonCategoryId] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [isCommitting, setIsCommitting] = useState(false);

  // ---------------------------------------------------------------------------
  // T08 Drawer mode state
  // ---------------------------------------------------------------------------

  /** Non-null when the draft has been promoted — switches footer to persistent-trade mode */
  const [promotedTrade, setPromotedTrade] = useState<Trade | null>(null);
  /** Error message from the last promote() attempt */
  const [promoteError, setPromoteError] = useState<string | null>(null);
  /** True while TradeService.updateWorkflowStatus() / updateShelved() is in flight */
  const [isWorkflowUpdating, setIsWorkflowUpdating] = useState(false);

  // ---------------------------------------------------------------------------
  // T09/T13: Execution state
  // ---------------------------------------------------------------------------

  /** ID of the instrument selected in draft mode */
  const [selectedInstrumentId, setSelectedInstrumentId] = useState('');
  /** T13: Snapshot of selected instrument — captured async on selection; used for promotion (T14) */
  const selectedInstrumentSnapshotRef = useRef<InstrumentSnapshot | undefined>(undefined);
  /** Tracks the latest instrument ID requested by handleInstrumentChange — used to discard stale async responses */
  const pendingInstrumentIdRef = useRef('');
  /** Reactive flag — true once the async instrument snapshot capture completes; used by promotion gate */
  const [instrumentSnapshotReady, setInstrumentSnapshotReady] = useState(false);
  /** TradeExecution projection — loaded on open in persistent-trade mode */
  const [tradeExecution, setTradeExecution] = useState<TradeExecution | null>(null);
  /** Execution event log — loaded on open in persistent-trade mode */
  const [tradeExecutionEvents, setTradeExecutionEvents] = useState<TradeExecutionEvent[]>([]);
  /** Qualitative review form state — placeholder; no persistence until WU2D/S03 */
  const [qualReview, setQualReview] = useState<QualitativeReviewSectionValue>({});

  // Log Fill / Log Close inline form state removed in T10.
  // T14 will build an Execution Event modal to replace the old inline forms.

  // ---------------------------------------------------------------------------
  // Risk context resolution state
  // ---------------------------------------------------------------------------

  /** Session-level risk snapshot — loaded from SessionDraft on drawer open */
  const [riskSnapshot, setRiskSnapshot] = useState<TradingSetupRiskSnapshot | undefined>(undefined);
  /** Resolved risk context ready for UI rendering (Tier 1 or Tier 2), or null (Tier 3 / unresolved) */
  const [resolvedRiskContext, setResolvedRiskContext] = useState<ResolvedRiskContext | null>(null);
  /** Tracks the latest resolution context (account|strategy|playbook) for Tier 2 async lookup — used to discard stale responses */
  const pendingRiskResolutionKeyRef = useRef('');
  /** Tracks the latest deviation note id for async enrichment — used to discard stale note responses */
  const pendingDeviationNoteIdRef = useRef('');
  /** Portal container for all drawer floating overlays — keeps them inside the drawer's accessibility tree */
  const [drawerPopoverContainer, setDrawerPopoverContainer] = useState<HTMLDivElement | null>(null);
  const setDrawerPopoverContainerRef = useCallback((node: HTMLDivElement | null) => setDrawerPopoverContainer(node), []);
  /** Shared popoverProps for all Select controls inside the drawer — keeps floating lists in the drawer's accessibility tree */
  const drawerSelectPopoverProps = {
    isNonModal: true,
    shouldBlockScroll: false,
    ...(drawerPopoverContainer ? { portalContainer: drawerPopoverContainer } : {}),
  };

  // ---------------------------------------------------------------------------
  // T10: Tab navigation + dirty tracking
  // ---------------------------------------------------------------------------

  /** Active tab. Smart default set in isOpen effect. */
  const [activeTab, setActiveTab] = useState<DrawerTab>('setup');
  /** True once any form field is changed in edit mode — drives Save Draft / Discard Changes CTAs */
  const [isDirty, setIsDirty] = useState(false);

  // ---------------------------------------------------------------------------
  // T14: Execution Event Modal state
  // ---------------------------------------------------------------------------

  /** True when the Execution Event modal is open */
  const [isExecutionModalOpen, setIsExecutionModalOpen] = useState(false);
  /** Mode for the Execution Event modal */
  const [executionModalMode, setExecutionModalMode] = useState<'order' | 'fill' | 'amend' | 'cancel'>('fill');
  /** The order-placed event targeted by an amend/cancel action (Fix #5A) */
  const [targetOrderEvent, setTargetOrderEvent] = useState<TradeExecutionEvent | null>(null);
  /** Pre-seeded direction for fill modal opened from "Order Filled" on an order row (Fix #5A) */
  const [fillPreseededDirection, setFillPreseededDirection] = useState<'long' | 'short' | undefined>(undefined);
  /** Pre-seeded quantity for fill modal opened from "Order Filled" on an order row (Fix #5A) */
  const [fillPreseededQuantity, setFillPreseededQuantity] = useState<number | undefined>(undefined);
  /** Pre-seeded price for fill modal opened from "Order Filled" on an order row (T16) */
  const [fillPreseededPrice, setFillPreseededPrice] = useState<number | undefined>(undefined);
  /** Maximum fill quantity — equals remaining order qty when opened from an order row (T16) */
  const [fillMaxQuantity, setFillMaxQuantity] = useState<number | undefined>(undefined);
  /** Initial fill sub-mode to pass to ExecutionEventModal on open ('entry' or 'exit') */
  const [fillInitialSubMode, setFillInitialSubMode] = useState<'entry' | 'exit' | undefined>(undefined);
  /** T02 (WU2E): initial order type for SL/TP placement modal */
  const [orderInitialType, setOrderInitialType] = useState<'limit' | 'stop' | undefined>(undefined);
  /** T02 (WU2E): when true, order type selector is hidden and shown as read-only label */
  const [orderLockType, setOrderLockType] = useState(false);
  /** T02 (WU2E): when true, direction is locked for SL/TP placement */
  const [modalLockDirection, setModalLockDirection] = useState(false);
  /** T02 (WU2E): prices of the opponent-type active orders, passed to modal for relational validation */
  const [modalRelationalOpponentPrices, setModalRelationalOpponentPrices] = useState<number[] | undefined>(undefined);
  /** T02 (WU2E): per-type cap override for SL/TP placement (stop cap for SL, limit cap for TP) */
  const [modalMaxOppositeQtyOverride, setModalMaxOppositeQtyOverride] = useState<number | undefined>(undefined);
  /** T03 (WU2E): active exit orders shown in fill modal cancel-allocation panel */
  const [activeExitOrdersForAllocation, setActiveExitOrdersForAllocation] = useState<Array<{
    id: string;
    orderType: 'stop' | 'limit';
    currentQuantity: number;
    filledQuantity: number;
    remainingQuantity: number;
    price: number | undefined;
  }>>([]);

  // ---------------------------------------------------------------------------
  // noteCategories from settings service
  // ---------------------------------------------------------------------------

  const [noteCategories, setNoteCategories] = useState<LiveTradingSettings['noteCategories']>([]);

  useEffect(() => {
    let mounted = true;
    liveTradingSettingsService.getOrCreateDefaultSettings().then((settings) => {
      if (mounted) {
        setNoteCategories(settings.noteCategories);
      }
    }).catch(() => { /* silent */ });
    return () => { mounted = false; };
  }, []);

  // ---------------------------------------------------------------------------
  // Active accounts — loaded once on mount for account picker and read-only display
  // ---------------------------------------------------------------------------

  const [activeAccounts, setActiveAccounts] = useState<Account[]>([]);

  useEffect(() => {
    let mounted = true;
    accountService.getActiveAccounts().then((accs) => {
      if (mounted) setActiveAccounts(accs);
    }).catch(() => { /* non-critical — leave list empty */ });
    return () => { mounted = false; };
  }, []);

  // ---------------------------------------------------------------------------
  // Refs — synced at render time so the isOpen effect reads latest values
  // without adding props to its dependency array (avoids re-runs mid-session).
  // ---------------------------------------------------------------------------

  /** True after any CTA successfully calls onCommit — skips note-draft cleanup */
  const isCommittedRef = useRef(false);
  /** Mode at open time — read in isOpen cleanup to skip note-draft delete in edit mode */
  const currentModeRef = useRef<'new' | 'edit'>(mode);
  currentModeRef.current = mode;
  /** existingDraft at open time — read by CTA handlers */
  const existingDraftRef = useRef<TradeSetupDraft | undefined>(existingDraft);
  existingDraftRef.current = existingDraft;
  /** logAsMissedPreOpen at open time */
  const logAsMissedPreOpenRef = useRef(logAsMissedPreOpen);
  logAsMissedPreOpenRef.current = logAsMissedPreOpen;
  /** quickTradePreset at open time — applied in isOpen effect new-mode branch */
  const quickTradePresetRef = useRef<QuickTradePreset | undefined>(quickTradePreset);
  quickTradePresetRef.current = quickTradePreset;
  /** Latest onAutoSave callback — synced so auto-save timer reads current value without effect re-runs */
  const onAutoSaveRef = useRef<typeof onAutoSave>(onAutoSave);
  onAutoSaveRef.current = onAutoSave;
  /** Latest onCancelDraft callback — synced so isOpen cleanup reads current value */
  const onCancelDraftRef = useRef<typeof onCancelDraft>(onCancelDraft);
  onCancelDraftRef.current = onCancelDraft;
  /** sessionDraftId at call time — synced for use in async CTA handlers */
  const sessionDraftIdRef = useRef<string | undefined>(sessionDraftId);
  sessionDraftIdRef.current = sessionDraftId;
  /** defaultAccountId synced so the isOpen effect seeds correctly without causing re-runs */
  const defaultAccountIdRef = useRef<string | undefined>(defaultAccountId);
  defaultAccountIdRef.current = defaultAccountId;
  /** Reference data refs — synced so isOpen effect reads latest values without causing re-runs */
  const activeStrategiesRef = useRef(activeStrategies);
  activeStrategiesRef.current = activeStrategies;
  const activePlaybooksRef = useRef(activePlaybooks);
  activePlaybooksRef.current = activePlaybooks;
  const activeCriteriaRef = useRef(activeCriteria);
  activeCriteriaRef.current = activeCriteria;
  const activeConfluencesRef = useRef(activeConfluences);
  activeConfluencesRef.current = activeConfluences;
  const activeEntryModelsRef = useRef(activeEntryModels);
  activeEntryModelsRef.current = activeEntryModels;

  /** Stable createdAt stamped once per new-mode open — preserved across auto-save calls */
  const draftCreatedAtRef = useRef('');
  /** Deferred preset hydration — set when Quick Trade preset opens but reference data isn't loaded yet */
  const needsPresetHydrationRef = useRef(false);
  /** Fix 1: ref-synced drawerImages — read in isOpen cleanup to delete orphaned uploaded images */
  const drawerImagesRef = useRef<DrawerImages>({});
  /** Pending auto-save debounce timer */
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** selectedAccountId synced so buildDraftForPromotion and async handlers read latest value */
  const selectedAccountIdRef = useRef<string>('');
  selectedAccountIdRef.current = selectedAccountId;
  /** T08: promotedTrade synced so handleMarkReady/handleComplete read the latest value */
  const promotedTradeRef = useRef<Trade | null>(null);

  // Sync draftId and drawerImages refs so the isOpen effect reads latest values on close
  draftIdRef.current = draftId;
  drawerImagesRef.current = drawerImages;
  promotedTradeRef.current = promotedTrade;

  // ---------------------------------------------------------------------------
  // Reset form on close; populate form on open
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let mounted = true;

    if (isOpen) {
      // Reset committed flag at the start of each open
      isCommittedRef.current = false;

      const existing = existingDraftRef.current;

      if (currentModeRef.current === 'edit' && existing) {
        // Edit mode — populate from existing TradeSetupDraft
        const p1 = existing.phase1;
        setDraftId(existing.id);
        setStrategyId(p1.strategySnapshot?.strategyId ?? '');
        setPlaybookId(p1.playbookSnapshot?.playbookId ?? '');
        setCriteria(p1.criteriaSnapshot ? [...p1.criteriaSnapshot] : []);
        setConfluences(p1.confluenceSnapshot ? [...p1.confluenceSnapshot] : []);
        setEntryModels(p1.entryModelSnapshot ? [...p1.entryModelSnapshot] : []);
        // tradeName is now on TradeDraftBase, not phase1
        setTradeName(existing.tradeName ?? '');
        setTradeNameManuallyEdited(true);
        setSelectedAccountId(existing.accountId ?? '');
        // Images — seeded into NotesSection via initialImages prop; also set
        // drawerImages so buildPhase1 has them before NotesSection syncs back.
        const phaseImages = p1.images ?? {};
        setDrawerImages({
          ...(phaseImages.order !== undefined ? { order: [...phaseImages.order] } : {}),
          ...(phaseImages.entry !== undefined ? { entry: [...phaseImages.entry] } : {}),
          ...(phaseImages.management !== undefined ? { management: [...phaseImages.management] } : {}),
          ...(phaseImages.exit !== undefined ? { exit: [...phaseImages.exit] } : {}),
        });
        // T10: edit mode starts clean
        setIsDirty(false);

        // T08/T09: Drawer mode detection — query Trade by rootDraftId.
        // If a Trade exists, this draft has been promoted → persistent-trade mode.
        // T09: also load TradeExecution + events for executionStatus-based CTA gates.
        if (existing.rootDraftId) {
          void tradeService.getByRootDraftId(existing.rootDraftId)
            .then(async (trade) => {
              if (!mounted) return;
              setPromotedTrade(trade ?? null);
              // T10: smart tab default based on promotion state
              if (!trade) {
                setActiveTab('setup');
              } else if (trade.workflowStatus === 'complete') {
                setActiveTab('review');
              } else {
                setActiveTab('execution');
              }
              if (!trade) return;
              const executions = await tradeExecutionService.getByTradeId(trade.id);
              const execution = executions[0] ?? null;
              if (!mounted) return;
              setTradeExecution(execution);
              if (!execution) return;
              const events = await tradeExecutionEventService.getByTradeExecutionId(execution.id);
              if (!mounted) return;
              setTradeExecutionEvents(events);
            })
            .catch(() => {
              // mode detection failure is silent — stays in draft mode
              if (mounted) setActiveTab('setup');
            });
        } else {
          setActiveTab('setup');
        }
      } else {
        // New mode — fresh draft
        const newId = generateUUID();
        setDraftId(newId);
        draftCreatedAtRef.current = new Date().toISOString();
        setTradeName(buildDefaultTradeName());
        setSelectedAccountId(defaultAccountIdRef.current ?? '');
        setDrawerImages({});
        setActiveTab('setup');
        setIsDirty(false);
        if (logAsMissedPreOpenRef.current) {
          setIsLogAsMissedOpen(true);
        }
        // Apply Quick Trade preset if provided
        const preset = quickTradePresetRef.current;
        if (preset) {
          if (preset.strategyId) setStrategyId(preset.strategyId);
          if (preset.playbookId) {
            setPlaybookId(preset.playbookId);
            const selectedPlaybook = activePlaybooksRef.current.find((p) => p.id === preset.playbookId);
            if (selectedPlaybook) {
              const allCriteriaSnaps = buildCriteriaSnapshots(selectedPlaybook, activeCriteriaRef.current);
              const checkedIds = preset.checkedCriteriaIds ?? [];
              setCriteria(allCriteriaSnaps.map((c) => ({ ...c, checked: checkedIds.includes(c.criteriaId) })));
              const allConfSnaps = buildConfluenceSnapshots(selectedPlaybook, activeConfluencesRef.current);
              const checkedCfIds = preset.checkedConfluenceIds ?? [];
              setConfluences(allConfSnaps.map((c) => ({ ...c, checked: checkedCfIds.includes(c.confluenceId) })));
              const allEMSnaps = buildEntryModelSnapshots(selectedPlaybook, activeEntryModelsRef.current);
              const selectedEMIds = preset.selectedEntryModelIds ?? [];
              setEntryModels(allEMSnaps.map((em) => ({ ...em, checked: selectedEMIds.includes(em.entryModelId) })));
            } else {
              // Reference data not loaded yet — defer hydration until data arrives
              needsPresetHydrationRef.current = true;
            }
            // Auto-generate trade name from preset
            const strat = activeStrategiesRef.current.find((s) => s.id === preset.strategyId);
            const pb = activePlaybooksRef.current.find((p) => p.id === preset.playbookId);
            if (strat && pb) setTradeName(buildAutoName(strat.name, pb.name));
          }
        }
      }

      // Load SessionDraft.riskSnapshot for risk context resolution.
      // Shared across edit/new — the sessionDraftId is the same regardless of mode.
      const capturedSessionDraftId = sessionDraftIdRef.current;
      if (capturedSessionDraftId) {
        void sessionDraftService.getById(capturedSessionDraftId)
          .then((draft) => {
            if (mounted) setRiskSnapshot(draft?.riskSnapshot);
          })
          .catch(() => { /* non-critical — Tier 1 stays unresolved */ });
      }
    } else {
      // Drawer closing — clean up orphaned note drafts, auto-saved draft, and uploaded images
      // (new mode only — edit mode does not create orphaned resources)
      const capturedDraftId = draftIdRef.current;
      if (capturedDraftId && !isCommittedRef.current && currentModeRef.current !== 'edit') {
        void noteDraftService.softDeleteByOwner('trade', capturedDraftId);
        void onCancelDraftRef.current?.(capturedDraftId);
        // Fix 1: delete any locally-uploaded images that were never committed
        const capturedImages = drawerImagesRef.current;
        const orphanedImageIds = Object.values(capturedImages)
          .flatMap((refs) => refs ?? [])
          .map((ref) => ref.imageId)
          .filter((id): id is string => id !== undefined);
        for (const imageId of orphanedImageIds) {
          void imageStorageService.deleteImage(imageId);
        }
      }
      // Clear any pending auto-save timer
      if (autoSaveTimerRef.current !== null) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      // Reset all form state
      setDraftId('');
      setTradeName('');
      setTradeNameManuallyEdited(false);
      setSelectedAccountId('');
      setStrategyId('');
      setPlaybookId('');
      setCriteria([]);
      setConfluences([]);
      setEntryModels([]);
      setDrawerImages({});
      setIsLogAsMissedOpen(false);
      setReasonCategoryId('');
      setReasonId('');
      setIsCommitting(false);
      // T08: reset mode state on close
      setPromotedTrade(null);
      setPromoteError(null);
      setIsWorkflowUpdating(false);
      // T09/T13: reset execution state on close
      setSelectedInstrumentId('');
      selectedInstrumentSnapshotRef.current = undefined;
      pendingInstrumentIdRef.current = ''; // clear pending request tracker
      setInstrumentSnapshotReady(false); // reset snapshot readiness
      setTradeExecution(null);
      setTradeExecutionEvents([]);
      setQualReview({} as QualitativeReviewSectionValue);
      // T10: reset tab + dirty state on close
      setActiveTab('setup');
      setIsDirty(false);
      // Close execution modal if open
      setIsExecutionModalOpen(false);
      // Reset risk context state
      setRiskSnapshot(undefined);
      setResolvedRiskContext(null);
      pendingRiskResolutionKeyRef.current = '';
      pendingDeviationNoteIdRef.current = '';
      // Clear deferred preset hydration flag
      needsPresetHydrationRef.current = false;
    }

    return () => { mounted = false; };
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Deferred preset hydration — re-applies Quick Trade preset snapshot
  // population when reference data arrives after the drawer has already opened.
  // One-shot: the flag is cleared immediately on first successful hydration.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!needsPresetHydrationRef.current || activePlaybooks.length === 0) return;
    needsPresetHydrationRef.current = false;
    const preset = quickTradePresetRef.current;
    if (!preset?.playbookId) return;
    const selectedPlaybook = activePlaybooks.find((p) => p.id === preset.playbookId);
    if (!selectedPlaybook) return;
    // Hydrate snapshots from preset checked-state
    const allCriteriaSnaps = buildCriteriaSnapshots(selectedPlaybook, activeCriteria);
    const checkedIds = preset.checkedCriteriaIds ?? [];
    setCriteria(allCriteriaSnaps.map((c) => ({ ...c, checked: checkedIds.includes(c.criteriaId) })));
    const allConfSnaps = buildConfluenceSnapshots(selectedPlaybook, activeConfluences);
    const checkedCfIds = preset.checkedConfluenceIds ?? [];
    setConfluences(allConfSnaps.map((c) => ({ ...c, checked: checkedCfIds.includes(c.confluenceId) })));
    const allEMSnaps = buildEntryModelSnapshots(selectedPlaybook, activeEntryModels);
    const selectedEMIds = preset.selectedEntryModelIds ?? [];
    setEntryModels(allEMSnaps.map((em) => ({ ...em, checked: selectedEMIds.includes(em.entryModelId) })));
    // Hydrate auto-name if the user hasn't manually edited it
    if (!tradeNameManuallyEdited) {
      const strat = activeStrategies.find((s) => s.id === preset.strategyId);
      const pb = activePlaybooks.find((p) => p.id === preset.playbookId);
      if (strat && pb) setTradeName(buildAutoName(strat.name, pb.name));
    }
  }, [activePlaybooks, activeCriteria, activeConfluences, activeEntryModels, activeStrategies, tradeNameManuallyEdited]);

  // ---------------------------------------------------------------------------
  // Risk context resolution — re-resolves when account, strategy, playbook,
  // or riskSnapshot changes. Tier 1 is synchronous; Tier 2 is async with a
  // race-condition guard (pendingRiskResolutionKeyRef).
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Derive mode-appropriate inputs — explicit split, no cross-mode fallback
    let effectiveAccountId: string;
    let effectiveStrategyId: string;
    let effectivePlaybookId: string;

    if (promotedTrade) {
      // Persistent-trade mode — wait for tradeExecution to load
      effectiveAccountId = tradeExecution?.accountId ?? '';
      effectiveStrategyId = promotedTrade.strategySnapshot?.strategyId ?? '';
      effectivePlaybookId = promotedTrade.playbookSnapshot?.playbookId ?? '';
    } else {
      // Draft mode — use drawer form state
      effectiveAccountId = selectedAccountId;
      effectiveStrategyId = strategyId;
      effectivePlaybookId = playbookId;
    }

    // No account selected → risk context is unresolvable
    if (!effectiveAccountId) {
      pendingRiskResolutionKeyRef.current = ''; // invalidate any in-flight Tier 2 lookup
      pendingDeviationNoteIdRef.current = '';
      setResolvedRiskContext(null);
      return;
    }

    // Tier 1 — synchronous: check session risk snapshot
    const tier1 = resolveRiskContextTier1({
      riskSnapshot,
      accountId: effectiveAccountId,
      strategyId: effectiveStrategyId,
      playbookId: effectivePlaybookId,
    });

    if (tier1) {
      pendingRiskResolutionKeyRef.current = ''; // invalidate any in-flight Tier 2 lookup
      setResolvedRiskContext(tier1);

      // Async enrichment: load deviation note text if present on the resolved context
      const noteId = tier1.deviationNoteId ?? '';
      pendingDeviationNoteIdRef.current = noteId;
      if (noteId) {
        void noteDraftService.get(noteId)
          .then((note) => {
            // Discard stale response if deviation note context changed while in flight
            if (pendingDeviationNoteIdRef.current !== noteId) return;
            if (note?.text) {
              setResolvedRiskContext((prev) =>
                prev && prev.provenance === 'pre-trading-plan'
                  ? { ...prev, deviationNoteText: note.text }
                  : prev
              );
            }
          })
          .catch(() => { /* non-critical — popover omits the row */ });
      }

      return;
    }

    // Tier 2 — async: look up account's default risk profile and run cascade
    pendingDeviationNoteIdRef.current = ''; // Tier 2 has no deviation note
    const capturedAccountId = effectiveAccountId;
    const capturedStrategyId = effectiveStrategyId;
    const capturedPlaybookId = effectivePlaybookId;
    const resolutionKey = `${capturedAccountId}|${capturedStrategyId}|${capturedPlaybookId}`;
    pendingRiskResolutionKeyRef.current = resolutionKey;

    void getDefaultRiskProfile(capturedAccountId, accountRiskService)
      .then((profile) => {
        // Discard stale response if resolution context changed while in flight
        if (resolutionKey !== pendingRiskResolutionKeyRef.current) return;

        if (profile) {
          const risk = computeEffectiveRisk(capturedStrategyId, capturedPlaybookId, profile);
          const derivation = determineDerivationSource(capturedStrategyId, capturedPlaybookId, profile);
          setResolvedRiskContext({
            provenance: 'account-default',
            risk,
            profileName: profile.name,
            derivedFromLevel: derivation.level,
            derivedFromStage: derivation.stage,
          });
        } else {
          // Tier 3 — neither tier resolved
          setResolvedRiskContext(null);
        }
      })
      .catch(() => {
        if (resolutionKey === pendingRiskResolutionKeyRef.current) {
          setResolvedRiskContext(null);
        }
      });
  }, [riskSnapshot, selectedAccountId, strategyId, playbookId, promotedTrade, tradeExecution]);

  // ---------------------------------------------------------------------------
  // Event handlers — strategy / playbook cascade
  // ---------------------------------------------------------------------------

  const handleStrategyChange = useCallback((newStrategyId: string): void => {
    setStrategyId(newStrategyId);
    setPlaybookId('');
    setCriteria([]);
    setConfluences([]);
    setEntryModels([]);
    setIsDirty(true); // T10
  }, []);

  const handlePlaybookChange = useCallback((newPlaybookId: string, currentStrategyId: string): void => {
    setPlaybookId(newPlaybookId);
    const selectedPlaybook = activePlaybooks.find((p) => p.id === newPlaybookId);
    if (selectedPlaybook) {
      setCriteria(buildCriteriaSnapshots(selectedPlaybook, activeCriteria));
      setConfluences(buildConfluenceSnapshots(selectedPlaybook, activeConfluences));
      setEntryModels(buildEntryModelSnapshots(selectedPlaybook, activeEntryModels));
    } else {
      setCriteria([]);
      setConfluences([]);
      setEntryModels([]);
    }

    if (!tradeNameManuallyEdited) {
      const strat = activeStrategies.find((s) => s.id === currentStrategyId);
      const pb = activePlaybooks.find((p) => p.id === newPlaybookId);
      if (strat && pb) {
        setTradeName(buildAutoName(strat.name, pb.name));
      }
    }
    setIsDirty(true); // T10
  }, [tradeNameManuallyEdited, activePlaybooks, activeCriteria, activeConfluences, activeEntryModels, activeStrategies]);

  const handleTradeNameChange = useCallback((value: string): void => {
    setTradeName(value);
    setTradeNameManuallyEdited(true);
    setIsDirty(true); // T10
  }, []);

  const handleReasonCategoryChange = useCallback((newCategoryId: string): void => {
    setReasonCategoryId(newCategoryId);
    setReasonId('');
  }, []);

  /**
   * T13: Instrument selection — sets selectedInstrumentId and async-captures the
   * InstrumentSnapshot for use during promotion (T14).
   */
  const handleInstrumentChange = useCallback((instrumentId: string): void => {
    setSelectedInstrumentId(instrumentId);
    pendingInstrumentIdRef.current = instrumentId; // track latest request for stale-response guard
    setInstrumentSnapshotReady(false); // reset until async capture completes
    selectedInstrumentSnapshotRef.current = undefined; // clear stale snapshot from previous instrument
    void instrumentService.get(instrumentId).then((instrument) => {
      if (instrument && instrumentId === pendingInstrumentIdRef.current) {
        selectedInstrumentSnapshotRef.current = buildInstrumentSnapshot(instrument);
        setInstrumentSnapshotReady(true); // snapshot captured — promotion gate can clear
      }
    }).catch(() => { /* silent — snapshot stays undefined; promotion gate stays closed */ });
  }, []);

  // ---------------------------------------------------------------------------
  // T14: Execution Event Modal handlers (part 1 — no buildPhase1 dependency)
  // ---------------------------------------------------------------------------

  /**
   * Open the Execution Event modal in the specified mode.
   * For 'fill' opened from an order row, pass the order event and remaining qty to
   * pre-seed direction/qty/price and cap the fill at the remaining order quantity.
   * For 'amend' / 'cancel', pass the order event so targetOrderEventId is threaded through.
   *
   * Not wrapped in useCallback — reads per-render values (orderRows, positionDirection)
   * that must be current at click time. Consistent with handleOpenSlModal/handleOpenTpModal.
   */
  const handleOpenExecutionModal = (
    eventMode: 'order' | 'fill' | 'amend' | 'cancel',
    orderEvent?: TradeExecutionEvent,
    orderRemainingQty?: number,
    fillSubModeHint?: 'entry' | 'exit',
    // Locked direction for "Add to Position" — pre-seeds direction without an order event.
    lockedDirection?: 'long' | 'short',
  ): void => {
    setExecutionModalMode(eventMode);
    setTargetOrderEvent(orderEvent ?? null);
    setFillInitialSubMode(fillSubModeHint);
    if (eventMode === 'fill' && orderEvent) {
      setFillPreseededDirection(orderEvent.direction);
      // Pre-seed quantity at remaining qty (= order qty on first fill; less on partial fill)
      setFillPreseededQuantity(orderRemainingQty ?? orderEvent.quantity);
      setFillPreseededPrice(orderEvent.price);
      setFillMaxQuantity(orderRemainingQty ?? orderEvent.quantity);
    } else {
      // When lockedDirection is provided (e.g. "Add to Position"), pre-seed it;
      // otherwise clear as before.
      setFillPreseededDirection(lockedDirection);
      setFillPreseededQuantity(undefined);
      setFillPreseededPrice(undefined);
      setFillMaxQuantity(undefined);
    }
    // T02 (WU2E): reset SL/TP-specific modal config for non-SL/TP opens.
    // For order mode, lock direction when a locked direction is provided (F-03 PR66:
    // once positionDirection is established, Add Order must be same-direction only).
    setOrderInitialType(undefined);
    setOrderLockType(false);
    setModalLockDirection(eventMode === 'order' && lockedDirection !== undefined);
    setModalRelationalOpponentPrices(undefined);
    setModalMaxOppositeQtyOverride(undefined);
    // T03 (WU2E): compute active exit orders for fill/exit modal cancel-allocation panel.
    // Only relevant when opening a fill modal (mode === 'fill').
    // Active exit orders = opposite direction, not cancelled/filled, stop or limit type,
    // excluding the order being directly filled (if any).
    if (eventMode === 'fill') {
      const exitOrders = orderRows.filter(r =>
        r.status !== 'cancelled' &&
        r.status !== 'filled' &&
        r.event.direction !== positionDirection &&
        (r.event.orderType === 'stop' || r.event.orderType === 'limit') &&
        r.event.id !== (orderEvent?.id),
      ).map(r => ({
        id: r.event.id,
        orderType: r.event.orderType as 'stop' | 'limit',
        currentQuantity: r.currentQuantity ?? 0,
        filledQuantity: r.filledQuantity,
        remainingQuantity: r.remainingQuantity ?? 0,
        price: r.currentPrice,
      }));
      setActiveExitOrdersForAllocation(exitOrders);
    } else {
      setActiveExitOrdersForAllocation([]);
    }
    setIsExecutionModalOpen(true);
  };

  /**
   * T02 (WU2E): Open the Execution Event modal to place a Stop Loss order.
   * Locks direction to the opposite of positionDirection and order type to 'stop'.
   * Uses the per-type SL cap and passes active TP prices for relational validation.
   *
   * Not wrapped in useCallback — captures per-render values (positionDirection,
   * activeTpPrices, maxSlOrderQty) which must always be current at click time.
   */
  const handleOpenSlModal = (): void => {
    if (positionDirection === undefined) return;
    const oppositeDir: 'long' | 'short' = positionDirection === 'long' ? 'short' : 'long';
    setExecutionModalMode('order');
    setTargetOrderEvent(null);
    setFillInitialSubMode(undefined);
    setFillPreseededDirection(oppositeDir);
    setFillPreseededQuantity(undefined);
    setFillPreseededPrice(undefined);
    setFillMaxQuantity(undefined);
    setOrderInitialType('stop');
    setOrderLockType(true);
    setModalLockDirection(true);
    setModalRelationalOpponentPrices(activeTpPrices);
    setModalMaxOppositeQtyOverride(maxSlOrderQty);
    setIsExecutionModalOpen(true);
  };

  /**
   * T02 (WU2E): Open the Execution Event modal to place a Take Profit order.
   * Locks direction to the opposite of positionDirection and order type to 'limit'.
   * Uses the per-type TP cap and passes active SL prices for relational validation.
   *
   * Not wrapped in useCallback — captures per-render values (positionDirection,
   * activeSlPrices, maxTpOrderQty) which must always be current at click time.
   */
  const handleOpenTpModal = (): void => {
    if (positionDirection === undefined) return;
    const oppositeDir: 'long' | 'short' = positionDirection === 'long' ? 'short' : 'long';
    setExecutionModalMode('order');
    setTargetOrderEvent(null);
    setFillInitialSubMode(undefined);
    setFillPreseededDirection(oppositeDir);
    setFillPreseededQuantity(undefined);
    setFillPreseededPrice(undefined);
    setFillMaxQuantity(undefined);
    setOrderInitialType('limit');
    setOrderLockType(true);
    setModalLockDirection(true);
    setModalRelationalOpponentPrices(activeSlPrices);
    setModalMaxOppositeQtyOverride(maxTpOrderQty);
    setIsExecutionModalOpen(true);
  };

  /**
   * T14: Reload Trade + TradeExecution + events after a successful execution action.
   * Transitions from draft mode to persistent-trade mode when a new trade is promoted.
   */
  const handleExecutionSuccess = useCallback(async (
    { tradeId, tradeExecutionId }: { tradeId: string; tradeExecutionId: string }
  ): Promise<void> => {
    const wasDraftMode = promotedTradeRef.current === null;
    // PR62 Fix #2: mark committed on promotion so drawer close does not delete image blobs
    if (wasDraftMode) {
      isCommittedRef.current = true;
    }
    const [trade, execution] = await Promise.all([
      tradeService.get(tradeId),
      tradeExecutionService.getById(tradeExecutionId),
    ]);
    if (trade) {
      setPromotedTrade(trade);
    }
    // Switch to Execution tab after first promotion
    if (wasDraftMode) {
      setActiveTab('execution');
    }
    setTradeExecution(execution ?? null);
    if (execution) {
      const events = await tradeExecutionEventService.getByTradeExecutionId(execution.id);
      setTradeExecutionEvents(events);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // buildPhase1 — assembles TradeSetupDraft.phase1 from current form state
  // tradeName is NOT included here — it belongs on TradeDraftBase
  // ---------------------------------------------------------------------------

  const buildPhase1 = useCallback((existingPhase1?: TradeSetupDraft['phase1']): TradeSetupDraft['phase1'] => {
    // Strip managed fields from existing; retain any forward-compat fields.
    // Strategy/playbook snapshots are captured as named vars so they can serve
    // as fallbacks when reference data hasn't loaded yet (race-condition guard).
    const {
      strategySnapshot: existingStratSnap, playbookSnapshot: existingPbSnap,
      criteriaSnapshot: _crit, confluenceSnapshot: _cf,
      entryModelSnapshot: _em, images: _imgs,
      ...forwardCompat
    } = existingPhase1 ?? {};
    const strat = strategyId
      ? activeStrategies.find((s) => s.id === strategyId)
      : undefined;
    const pb = playbookId
      ? activePlaybooks.find((p) => p.id === playbookId)
      : undefined;
    return {
      ...forwardCompat,
      ...(strat !== undefined
        ? { strategySnapshot: buildStrategySnapshot(strat) }
        : existingStratSnap !== undefined
          ? { strategySnapshot: existingStratSnap }
          : {}),
      ...(pb !== undefined
        ? { playbookSnapshot: buildPlaybookSnapshot(pb) }
        : existingPbSnap !== undefined
          ? { playbookSnapshot: existingPbSnap }
          : {}),
      ...(criteria.length > 0 ? { criteriaSnapshot: criteria } : {}),
      ...(confluences.length > 0 ? { confluenceSnapshot: confluences } : {}),
      ...(entryModels.length > 0 ? { entryModelSnapshot: entryModels } : {}),
      ...(Object.keys(drawerImages).length > 0 ? { images: drawerImages } : {}),
    };
  }, [strategyId, playbookId, criteria, confluences, entryModels, drawerImages, activeStrategies, activePlaybooks]);

  // ---------------------------------------------------------------------------
  // T14: Execution Event Modal handlers (part 2 — requires buildPhase1)
  // ---------------------------------------------------------------------------

  /**
   * T14: Build a TradeSetupDraft snapshot for promotion from current drawer state.
   * Injects accountId so TradePromotionService.promote() pre-flight passes
   * the draft.accountId === input.accountId check.
   */
  const buildDraftForPromotion = useCallback((): TradeSetupDraft => {
    const existing = existingDraftRef.current;
    if (existing) {
      // PR62 Fix #3: rebuild with current form state so edits are not lost on promotion
      return {
        ...existing,
        updatedAt: new Date().toISOString(),
        ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
        phase1: buildPhase1(existing.phase1),
        ...(selectedAccountIdRef.current ? { accountId: selectedAccountIdRef.current } : {}),
      };
    }
    // New mode — build from current form state
    const capturedId = draftIdRef.current;
    const sid = sessionDraftIdRef.current ?? '';
    const now = new Date().toISOString();
    return {
      id: capturedId,
      rootDraftId: capturedId,
      sessionDraftId: sid,
      originSessionDraftId: sid,
      createdAt: draftCreatedAtRef.current || now,
      updatedAt: now,
      artifactKind: 'trade',
      workflowStatus: 'pending',
      isShelved: false,
      carryForwardDecision: 'undecided',
      ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
      phase1: buildPhase1(),
      ...(selectedAccountIdRef.current ? { accountId: selectedAccountIdRef.current } : {}),
    };
  }, [tradeName, buildPhase1]);

  // ---------------------------------------------------------------------------
  // Auto-save — debounced 1.5s after last form change (new mode only)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen || mode !== 'new' || !draftId || (!tradeName && !strategyId)) return;

    if (autoSaveTimerRef.current !== null) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      const capturedId = draftIdRef.current;
      if (!capturedId) return;
      const sessionId = sessionDraftIdRef.current ?? '';
      const draft: TradeSetupDraft = {
        id: capturedId,
        rootDraftId: capturedId,
        sessionDraftId: sessionId,
        originSessionDraftId: sessionId,
        createdAt: draftCreatedAtRef.current,
        updatedAt: new Date().toISOString(),
        artifactKind: 'trade',
        workflowStatus: 'pending',
        isShelved: false,
        carryForwardDecision: 'undecided',
        ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
        phase1: buildPhase1(),
        ...(selectedAccountIdRef.current ? { accountId: selectedAccountIdRef.current } : {}),
      };
      void onAutoSaveRef.current?.(draft);
    }, 1500);

    return () => {
      if (autoSaveTimerRef.current !== null) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [isOpen, mode, draftId, buildPhase1, tradeName, strategyId, selectedAccountId]);

  // ---------------------------------------------------------------------------
  // CTA handlers
  // ---------------------------------------------------------------------------

  /**
   * T10: Save the current form as a non-shelved draft and close.
   * New mode only — the primary "Save Draft ✓" CTA.
   */
  const handleSaveDraft = useCallback(async (): Promise<void> => {
    if (!onCommit || isCommitting) return;
    if (autoSaveTimerRef.current !== null) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setIsCommitting(true);
    const now = new Date().toISOString();
    const sessionId = sessionDraftIdRef.current ?? '';
    const draft: TradeSetupDraft = {
      id: draftIdRef.current,
      rootDraftId: draftIdRef.current,
      sessionDraftId: sessionId,
      originSessionDraftId: sessionId,
      createdAt: draftCreatedAtRef.current || now,
      updatedAt: now,
      artifactKind: 'trade',
      workflowStatus: 'pending',
      isShelved: false,
      carryForwardDecision: 'undecided',
      ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
      phase1: buildPhase1(),
      ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
    };
    isCommittedRef.current = true;
    try {
      await onCommit(draft);
      onClose();
    } catch {
      isCommittedRef.current = false;
    } finally {
      setIsCommitting(false);
    }
  }, [onCommit, isCommitting, buildPhase1, onClose, tradeName, selectedAccountId]);

  const handleShelve = useCallback(async (): Promise<void> => {
    if (!onCommit || isCommitting) return;
    // Bug 1: clear pending auto-save so it cannot overwrite this commit
    if (autoSaveTimerRef.current !== null) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setIsCommitting(true);
    const now = new Date().toISOString();
    const sessionId = sessionDraftIdRef.current ?? '';
    const draft: TradeSetupDraft = {
      id: draftIdRef.current,
      rootDraftId: draftIdRef.current,
      sessionDraftId: sessionId,
      originSessionDraftId: sessionId,
      createdAt: now,
      updatedAt: now,
      artifactKind: 'trade',
      workflowStatus: 'pending',
      isShelved: true,
      carryForwardDecision: 'undecided',
      ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
      phase1: buildPhase1(),
      ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
    };
    isCommittedRef.current = true;
    try {
      await onCommit(draft);
      onClose();
    } catch {
      isCommittedRef.current = false;
    } finally {
      setIsCommitting(false);
    }
  }, [onCommit, isCommitting, buildPhase1, onClose, tradeName, selectedAccountId]);

  const handleSaveChanges = useCallback(async (): Promise<void> => {
    if (!onCommit || isCommitting) return;
    const existing = existingDraftRef.current;
    if (!existing) return;
    // Bug 1: clear pending auto-save so it cannot overwrite this commit
    if (autoSaveTimerRef.current !== null) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setIsCommitting(true);
    const { tradeName: _prevName, accountId: _acct, ...existingBase } = existing;
    const draft: TradeSetupDraft = {
      ...existingBase,
      updatedAt: new Date().toISOString(),
      ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
      phase1: buildPhase1(existing.phase1),
      ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
    };
    isCommittedRef.current = true;
    try {
      await onCommit(draft);
      onClose();
    } catch {
      isCommittedRef.current = false;
    } finally {
      setIsCommitting(false);
    }
  }, [onCommit, isCommitting, buildPhase1, onClose, tradeName, selectedAccountId]);

  const handleShelveEdit = useCallback(async (): Promise<void> => {
    if (!onCommit || isCommitting) return;
    const existing = existingDraftRef.current;
    if (!existing) return;
    // Bug 1: clear pending auto-save so it cannot overwrite this commit
    if (autoSaveTimerRef.current !== null) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setIsCommitting(true);
    const { tradeName: _prevName, accountId: _acct, ...existingBase } = existing;
    const draft: TradeSetupDraft = {
      ...existingBase,
      isShelved: true,
      updatedAt: new Date().toISOString(),
      ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
      phase1: buildPhase1(existing.phase1),
      ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
    };
    isCommittedRef.current = true;
    try {
      await onCommit(draft);
      onClose();
    } catch {
      isCommittedRef.current = false;
    } finally {
      setIsCommitting(false);
    }
  }, [onCommit, isCommitting, buildPhase1, onClose, tradeName, selectedAccountId]);

  const handleReactivate = useCallback(async (): Promise<void> => {
    if (!onCommit || isCommitting) return;
    const existing = existingDraftRef.current;
    if (!existing) return;
    // Bug 1: clear pending auto-save so it cannot overwrite this commit
    if (autoSaveTimerRef.current !== null) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setIsCommitting(true);
    const { tradeName: _prevName, accountId: _acct, ...existingBase } = existing;
    const draft: TradeSetupDraft = {
      ...existingBase,
      workflowStatus: 'active',
      isShelved: false,
      updatedAt: new Date().toISOString(),
      ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
      phase1: buildPhase1(existing.phase1),
      ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
    };
    isCommittedRef.current = true;
    try {
      await onCommit(draft);
      onClose();
    } catch {
      isCommittedRef.current = false;
    } finally {
      setIsCommitting(false);
    }
  }, [onCommit, isCommitting, buildPhase1, onClose, tradeName, selectedAccountId]);

  /**
   * T10: Discard all pending edit-mode form changes and reset to existingDraft values.
   * Clears isDirty — footer reverts to "Saved draft clean" state.
   */
  const handleDiscardChanges = useCallback((): void => {
    const existing = existingDraftRef.current;
    if (!existing) return;
    const p1 = existing.phase1;
    setStrategyId(p1.strategySnapshot?.strategyId ?? '');
    setPlaybookId(p1.playbookSnapshot?.playbookId ?? '');
    setCriteria(p1.criteriaSnapshot ? [...p1.criteriaSnapshot] : []);
    setConfluences(p1.confluenceSnapshot ? [...p1.confluenceSnapshot] : []);
    setEntryModels(p1.entryModelSnapshot ? [...p1.entryModelSnapshot] : []);
    setTradeName(existing.tradeName ?? '');
    setTradeNameManuallyEdited(true);
    setSelectedAccountId(existing.accountId ?? '');
    setIsDirty(false);
  }, []);

  const handleLogAsMissedConfirm = useCallback(async (): Promise<void> => {
    if (!onCommit || isCommitting || !reasonCategoryId || !reasonId) return;
    const selectedCategory = MOCK_REASON_CATEGORIES.find((c) => c.categoryId === reasonCategoryId);
    const selectedReasonData = getMockReasonsForCategory(reasonCategoryId)
      .find((r) => r.snapshot.reasonId === reasonId);
    if (!selectedCategory || !selectedReasonData) return;
    // Bug 1: clear pending auto-save so it cannot overwrite this commit
    if (autoSaveTimerRef.current !== null) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setIsCommitting(true);
    const now = new Date().toISOString();
    const existing = existingDraftRef.current;
    const sessionId = sessionDraftIdRef.current ?? '';

    const phase2Opportunity: NonNullable<OpportunityDraft['phase2Opportunity']> = {
      reasonCategorySnapshot: {
        categoryId: selectedCategory.categoryId,
        name: selectedCategory.name,
        ...(selectedCategory.description !== undefined ? { description: selectedCategory.description } : {}),
      },
      reasonSnapshot: {
        reasonId: selectedReasonData.snapshot.reasonId,
        name: selectedReasonData.snapshot.name,
        ...(selectedReasonData.snapshot.description !== undefined ? { description: selectedReasonData.snapshot.description } : {}),
      },
    };

    // Build OpportunityDraft from scratch — cannot spread TradeSetupDraft since types differ
    const baseId = existing?.id ?? draftIdRef.current;
    const draft: OpportunityDraft = {
      id: baseId,
      rootDraftId: existing?.rootDraftId ?? baseId,
      sessionDraftId: existing?.sessionDraftId ?? sessionId,
      originSessionDraftId: existing?.originSessionDraftId ?? sessionId,
      ...(existing?.carriedFromDraftId !== undefined ? { carriedFromDraftId: existing.carriedFromDraftId } : {}),
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      artifactKind: 'opportunity',
      opportunityKind: 'missed',
      workflowStatus: 'complete',
      isShelved: false,
      carryForwardDecision: existing?.carryForwardDecision ?? 'undecided',
      ...(tradeName.trim() ? { tradeName: tradeName.trim() } : {}),
      phase2Opportunity,
    };

    isCommittedRef.current = true;
    try {
      await onCommit(draft);
      onClose();
    } catch {
      isCommittedRef.current = false;
    } finally {
      setIsCommitting(false);
    }
  }, [onCommit, isCommitting, reasonCategoryId, reasonId, onClose, tradeName]);

  const handleCancelLogAsMissed = useCallback((): void => {
    setIsLogAsMissedOpen(false);
    setReasonCategoryId('');
    setReasonId('');
  }, []);

  // ---------------------------------------------------------------------------
  // T08: Promotion + workflow handlers
  // ---------------------------------------------------------------------------

  // handlePromote removed in T10 — T13 will add promotion triggered by first execution event.

  /**
   * Advance workflowStatus to 'ready-for-review'.
   * Enabled when executionStatus is terminal (closed or cancelled) — see canMarkReady.
   */
  const handleMarkReady = useCallback(async (): Promise<void> => {
    const trade = promotedTradeRef.current;
    if (!trade || isWorkflowUpdating) return;
    setIsWorkflowUpdating(true);
    setPromoteError(null);
    try {
      const updated = await tradeService.updateWorkflowStatus(trade.id, 'ready-for-review');
      setPromotedTrade(updated);
    } catch {
      setPromoteError('Failed to mark trade as ready. Please try again.');
    } finally {
      setIsWorkflowUpdating(false);
    }
  }, [isWorkflowUpdating]);

  /**
   * Advance workflowStatus to 'complete'.
   * Available when workflowStatus === 'ready-for-review' in persistent-trade mode.
   */
  const handleComplete = useCallback(async (): Promise<void> => {
    const trade = promotedTradeRef.current;
    if (!trade || isWorkflowUpdating) return;
    setIsWorkflowUpdating(true);
    setPromoteError(null);
    try {
      const updated = await tradeService.updateWorkflowStatus(trade.id, 'complete');
      setPromotedTrade(updated);
      setActiveTab('review');
    } catch {
      setPromoteError('Failed to complete trade. Please try again.');
    } finally {
      setIsWorkflowUpdating(false);
    }
  }, [isWorkflowUpdating]);

  /**
   * T10: Toggle isShelved on the promoted Trade record.
   * Persistent-trade mode Shelve/Unshelve footer action.
   */
  const handleShelveTogglePersistent = useCallback(async (): Promise<void> => {
    const trade = promotedTradeRef.current;
    if (!trade || isWorkflowUpdating) return;
    setIsWorkflowUpdating(true);
    setPromoteError(null);
    try {
      const updated = await tradeService.updateShelved(trade.id, !trade.isShelved);
      setPromotedTrade(updated);
    } catch {
      setPromoteError('Failed to update shelve status. Please try again.');
    } finally {
      setIsWorkflowUpdating(false);
    }
  }, [isWorkflowUpdating]);

  // handleLogFill / handleLogClose removed in T10 — T14 will add Execution Event modal.

  // ---------------------------------------------------------------------------
  // Derived: playbooks filtered by selected strategy; filtered reasons
  // ---------------------------------------------------------------------------

  const availablePlaybooks = strategyId
    ? activePlaybooks.filter((p) => {
        const strategy = activeStrategies.find((s) => s.id === strategyId);
        return strategy?.playbookIds.includes(p.id) ?? false;
      })
    : [];
  const filteredReasons = reasonCategoryId ? getMockReasonsForCategory(reasonCategoryId) : [];

  // T10: Mark Ready requires terminal execution state (closed or cancelled).
  // Correctness fix #4 from PR61 — exit preconditions enforced at service level;
  // this is the UI-level gate matching the design spec.
  const canMarkReady =
    (tradeExecution?.executionStatus === 'closed' || tradeExecution?.executionStatus === 'cancelled') &&
    promotedTrade?.workflowStatus !== 'ready-for-review' &&
    promotedTrade?.workflowStatus !== 'complete';

  const canMarkComplete = promotedTrade?.workflowStatus === 'ready-for-review';

  // ---------------------------------------------------------------------------
  // Section content nodes
  // ---------------------------------------------------------------------------

  const strategyPlaybookContent = (
    <div className="flex flex-col gap-3">
      <Select
        label="Strategy"
        size="sm"
        variant="bordered"
        selectedKeys={strategyId ? [strategyId] : []}
        onSelectionChange={(keys) => {
          if (keys === 'all') return;
          const key = Array.from(keys)[0] as string | undefined;
          handleStrategyChange(key ?? '');
        }}
        popoverProps={drawerSelectPopoverProps}
        {...(!isDesktop ? { classNames: { value: 'text-base' } } : {})}
      >
        {activeStrategies.map((s) => (
          <SelectItem key={s.id}>{s.name}</SelectItem>
        ))}
      </Select>

      <Select
        label="Playbook"
        size="sm"
        variant="bordered"
        isDisabled={!strategyId}
        selectedKeys={playbookId ? [playbookId] : []}
        onSelectionChange={(keys) => {
          if (keys === 'all') return;
          const key = Array.from(keys)[0] as string | undefined;
          if (key) handlePlaybookChange(key, strategyId);
        }}
        popoverProps={drawerSelectPopoverProps}
        {...(!isDesktop ? { classNames: { value: 'text-base' } } : {})}
      >
        {availablePlaybooks.map((p) => (
          <SelectItem key={p.id}>{p.name}</SelectItem>
        ))}
      </Select>
    </div>
  );

  // T10: onChange wrappers include setIsDirty(true) for edit-mode dirty tracking
  const criteriaContent = (
    <CriteriaSection
      criteria={criteria}
      onChange={(c) => { setCriteria(c); setIsDirty(true); }}
    />
  );

  const confluenceContent = (
    <ConfluenceSection
      confluences={confluences}
      onChange={(c) => { setConfluences(c); setIsDirty(true); }}
    />
  );

  const entryModelsContent = (
    <EntryModelsSection
      entryModels={entryModels}
      onChange={(em) => { setEntryModels(em); setIsDirty(true); }}
    />
  );

  // Compute initialImages for NotesSection.
  // Fix #7: In persistent-trade mode, seed from Trade.images (durable, set by updateImages).
  //         In draft mode, fall back to phase1.images from the existing draft.
  const notesSectionInitialImages: DrawerImages | undefined = (() => {
    if (promotedTrade !== null) {
      const ti = promotedTrade.images;
      if (!ti) return undefined;
      return {
        ...(ti.order !== undefined ? { order: [...ti.order] } : {}),
        ...(ti.entry !== undefined ? { entry: [...ti.entry] } : {}),
        ...(ti.management !== undefined ? { management: [...ti.management] } : {}),
        ...(ti.exit !== undefined ? { exit: [...ti.exit] } : {}),
      };
    }
    if (mode !== 'edit' || !existingDraft) return undefined;
    const p = existingDraft.phase1.images;
    if (!p) return undefined;
    return {
      ...(p.order !== undefined ? { order: [...p.order] } : {}),
      ...(p.entry !== undefined ? { entry: [...p.entry] } : {}),
      ...(p.management !== undefined ? { management: [...p.management] } : {}),
      ...(p.exit !== undefined ? { exit: [...p.exit] } : {}),
    };
  })();

  const notesContent = (
    <NotesSection
      draftId={draftId}
      noteCategories={noteCategories}
      onImagesChange={setDrawerImages}
      onImagesDirty={() => { setIsDirty(true); }}
      // T12: pass tradeId in persistent-trade mode so notes write to Note table (ownerId=tradeId)
      {...(promotedTrade !== null ? { tradeId: promotedTrade.id } : {})}
      {...(notesSectionInitialImages !== undefined ? { initialImages: notesSectionInitialImages } : {})}
    />
  );

  // T13: Execution tab — InstrumentDropdown + state-driven execution panel.
  // States: draft (pre-promotion) | empty | pending | open | partial-exit | closed | cancelled
  const executionStatus = tradeExecution?.executionStatus ?? null;
  const sortedExecutionEvents = tradeExecutionEvents.length > 0
    ? [...tradeExecutionEvents].sort((a, b) => {
        const byOccurredAt = a.occurredAt.localeCompare(b.occurredAt);
        if (byOccurredAt !== 0) return byOccurredAt;
        // Tiebreaker: createdAt (DB write time) preserves insertion order for
        // same-minute events (datetime-local input has minute precision only).
        return a.createdAt.localeCompare(b.createdAt);
      })
    : [];

  // T16: derive per-order rows from event log (Option A — no projection change).
  // Each order-placed event = one row; status derived from linked amend/cancel/fill events.
  // 'filled' = fully filled (disappears from active orders, shown in history as "Order Filled").
  // Partial fills show remaining quantity and keep Filled/Amend/Cancel actions.
  type OrderRowStatus = 'pending' | 'amended' | 'cancelled' | 'filled';
  interface OrderRow {
    event: TradeExecutionEvent;
    status: OrderRowStatus;
    currentQuantity: number | undefined;
    currentPrice: number | undefined;
    filledQuantity: number;
    remainingQuantity: number | undefined;
  }
  const orderRows: OrderRow[] = (() => {
    const amendments = sortedExecutionEvents.filter(e => e.eventType === 'order-amended');
    const cancellations = sortedExecutionEvents.filter(e => e.eventType === 'order-cancelled');
    // Fills/exits that carry a targetOrderEventId link back to their originating order.
    // Includes entry-fill (same-direction order fill) and partial-exit/final-exit
    // (opposite-direction order fill routed through logClose via F-07 logic).
    const linkedFills = sortedExecutionEvents.filter(
      e => (e.eventType === 'entry-fill' || e.eventType === 'partial-exit' || e.eventType === 'final-exit') &&
        e.targetOrderEventId !== undefined
    );
    return sortedExecutionEvents
      .filter(e => e.eventType === 'order-placed')
      .map(order => {
        const isCancelled = cancellations.some(e => e.targetOrderEventId === order.id);
        const orderAmends = amendments.filter(e => e.targetOrderEventId === order.id);
        // Resolve qty and price independently — a price-only amend must not mask a prior
        // qty amend, and vice versa (mirrors the pattern in resolveActiveOppositeOrders).
        const reduceLatest = (evts: typeof orderAmends) =>
          evts.reduce((a, b) => {
            if (a.occurredAt !== b.occurredAt) return a.occurredAt > b.occurredAt ? a : b;
            return a.createdAt > b.createdAt ? a : b;
          });
        const qtyAmends = orderAmends.filter(e => e.quantity !== undefined);
        const priceAmends = orderAmends.filter(e => e.price !== undefined);
        const latestQtyAmend = qtyAmends.length > 0 ? reduceLatest(qtyAmends) : undefined;
        const latestPriceAmend = priceAmends.length > 0 ? reduceLatest(priceAmends) : undefined;
        const currentQuantity = latestQtyAmend?.quantity ?? order.quantity;
        const currentPrice = latestPriceAmend?.price ?? order.price;

        // Sum all fill quantities linked to this order
        const filledQuantity = linkedFills
          .filter(e => e.targetOrderEventId === order.id)
          .reduce((sum, e) => sum + (e.quantity ?? 0), 0);
        const isFullyFilled = currentQuantity !== undefined && filledQuantity >= currentQuantity;

        const status = (
          isCancelled ? 'cancelled' :
          isFullyFilled ? 'filled' :
          (latestQtyAmend || latestPriceAmend) ? 'amended' : 'pending'
        ) as OrderRowStatus;

        const remainingQuantity = currentQuantity !== undefined
          ? currentQuantity - filledQuantity
          : undefined;

        return { event: order, status, currentQuantity, currentPrice, filledQuantity, remainingQuantity };
      });
  })();

  // F-07 (PR62): derive position direction from the first entry-fill event.
  // Opposite-direction order fills must route to logClose() (exit) not logFill() (entry).
  const positionDirection = sortedExecutionEvents.find(e => e.eventType === 'entry-fill')?.direction;
  // F-03 (PR66): for "+ Add Order" direction lock, derive from current committed state:
  // 1. positionDirection (from first entry-fill) — highest priority
  // 2. direction of any active (non-cancelled, non-filled) order — intent still live
  // 3. undefined — no committed intent, direction selector is free
  const activeOrderDirection = orderRows.find(r => r.status !== 'cancelled' && r.status !== 'filled')?.event.direction;
  const addOrderLockedDirection = positionDirection ?? activeOrderDirection;

  // Maximum quantity available for a new opposite-direction order.
  // = position remaining qty minus the total remaining qty of active opposite-direction orders.
  // Clamped to 0 to handle already-overreserved data from the existing bug.
  const maxOppositeOrderQty =
    positionDirection !== undefined && tradeExecution?.remainingQuantity !== undefined
      ? computeMaxOppositeOrderQty(sortedExecutionEvents, positionDirection, tradeExecution.remainingQuantity)
      : undefined;

  // T02 (WU2E): per-type caps for SL (stop) and TP (limit) placement.
  const maxSlOrderQty =
    positionDirection !== undefined && tradeExecution?.remainingQuantity !== undefined
      ? computeMaxOppositeOrderQty(sortedExecutionEvents, positionDirection, tradeExecution.remainingQuantity, 'stop')
      : undefined;

  const maxTpOrderQty =
    positionDirection !== undefined && tradeExecution?.remainingQuantity !== undefined
      ? computeMaxOppositeOrderQty(sortedExecutionEvents, positionDirection, tradeExecution.remainingQuantity, 'limit')
      : undefined;

  // T02 (WU2E): prices of active SL/TP orders — used as opponent prices in the placement modal.
  const activeSlPrices =
    positionDirection !== undefined
      ? getActiveOppositeOrderPrices(sortedExecutionEvents, positionDirection, 'stop')
      : [];

  const activeTpPrices =
    positionDirection !== undefined
      ? getActiveOppositeOrderPrices(sortedExecutionEvents, positionDirection, 'limit')
      : [];

  // Maximum quantity for an opposite-direction order amendment.
  // Adds back the order's own reservation (being replaced, not added) to the general pool.
  const maxAmendQty = (() => {
    if (executionModalMode !== 'amend' || targetOrderEvent === null || positionDirection === undefined) return undefined;
    const orderRow = orderRows.find(r => r.event.id === targetOrderEvent.id);
    if (!orderRow || orderRow.event.direction === undefined || orderRow.event.direction === positionDirection) return undefined;
    return (maxOppositeOrderQty ?? 0) + (orderRow.remainingQuantity ?? 0);
  })();

  // ── Promotion gate — computed once per render, consumed by buttons + gate message ──
  const PROMOTION_FIELD_LABELS: Record<string, string> = {
    accountId: 'Account',
    strategySnapshot: 'Strategy',
    playbookSnapshot: 'Playbook',
  };
  const promotionResult = promotedTrade === null
    ? checkPromotionReadiness(buildDraftForPromotion())
    : { isReady: true as const, missingFields: [] as string[] };
  const isInstrumentMissing = selectedInstrumentId === '';
  const isInstrumentLoading = selectedInstrumentId !== '' && !instrumentSnapshotReady;
  const missingLabels = [
    ...promotionResult.missingFields.map((f) => PROMOTION_FIELD_LABELS[f] ?? f),
    ...(isInstrumentMissing ? ['Instrument'] : []),
  ];
  const isPromotionGateOpen = promotionResult.isReady && !isInstrumentMissing && !isInstrumentLoading;
  const hasSetupTabBlockers = promotionResult.missingFields.includes('strategySnapshot')
    || promotionResult.missingFields.includes('playbookSnapshot');

  const executionContent = (
    <div className="flex flex-col gap-4">

      {/* ── Account ─────────────────────────────── */}
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
          Account
        </p>
        {promotedTrade === null ? (
          <>
            <Select
              size="sm"
              variant="bordered"
              label="Account"
              placeholder={activeAccounts.length === 0 ? 'No accounts — add one in Settings' : 'Select account'}
              selectedKeys={selectedAccountId ? new Set([selectedAccountId]) : new Set()}
              onSelectionChange={(keys) => {
                const val = Array.from(keys)[0] as string ?? '';
                setSelectedAccountId(val);
                if (mode === 'edit') setIsDirty(true);
              }}
              popoverProps={drawerSelectPopoverProps}
            >
              {activeAccounts.map((acc) => (
                <SelectItem key={acc.id}>{acc.name}</SelectItem>
              ))}
            </Select>
            {!isPromotionGateOpen && promotionResult.missingFields.includes('accountId') && (
              <p className="text-xs text-warning-600 dark:text-warning-400">Select an account</p>
            )}
          </>
        ) : (
          <p className="text-sm text-text-primary">
            {tradeExecution !== null
              ? (activeAccounts.find((a) => a.id === tradeExecution.accountId)?.name ?? tradeExecution.accountId)
              : '—'}
          </p>
        )}
      </div>

      {/* ── Instrument picker ───────────────────── */}
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
          Instrument
        </p>
        <InstrumentDropdown
          value={
            promotedTrade !== null
              ? (tradeExecution?.instrumentSnapshot?.instrumentId ?? null)
              : (selectedInstrumentId || null)
          }
          onChange={handleInstrumentChange}
          size="sm"
          popoverProps={drawerSelectPopoverProps}
          {...(promotedTrade !== null ? { isDisabled: true } : {})}
        />
        {!isPromotionGateOpen && promotedTrade === null && (isInstrumentMissing || isInstrumentLoading) && (
          <p className="text-xs text-warning-600 dark:text-warning-400">
            {isInstrumentLoading ? 'Loading instrument details\u2026' : 'Select an instrument'}
          </p>
        )}
      </div>

      {/* ── Risk context block (read-only) ────────── */}
      {resolvedRiskContext !== null && (() => {
        const riskAccountId = promotedTrade !== null ? (tradeExecution?.accountId ?? '') : selectedAccountId;
        const riskCurrency = activeAccounts.find(a => a.id === riskAccountId)?.baseCurrency || 'USD';
        const confirmedInPreTrading = resolvedRiskContext.provenance === 'pre-trading-plan';
        const derivedFromLabels: Record<string, string> = {
          'account-default': 'Account Default',
          'strategy-override': 'Strategy Override',
          'playbook-override': 'Playbook Override',
        };
        const derivedFromLabel = resolvedRiskContext.derivedFromLevel
          ? derivedFromLabels[resolvedRiskContext.derivedFromLevel] ?? resolvedRiskContext.derivedFromLevel
          : undefined;
        const isSessionOverride = resolvedRiskContext.derivedFromStage === 'session';
        const showDividerSection = isSessionOverride || !!resolvedRiskContext.deviationNoteText;
        return (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
              Risk
            </p>
            <div className="bg-default-50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-text-primary">
                  {formatRisk(resolvedRiskContext.risk, riskCurrency)}
                </span>
                <span className="text-sm text-text-secondary ml-auto">
                  {resolvedRiskContext.profileName}
                </span>
                <Popover
                  placement="left"
                  isNonModal
                  shouldBlockScroll={false}
                  {...(drawerPopoverContainer && {
                    portalContainer: drawerPopoverContainer,
                  })}
                >
                  <PopoverTrigger>
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-default-100 text-text-secondary hover:text-text-primary transition-colors"
                      aria-label="Risk details"
                    >
                      <Info size={14} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className="p-3 flex flex-col gap-2 text-sm min-w-50">
                      {derivedFromLabel && (
                        <div className="flex justify-between gap-4">
                          <span className="text-text-secondary">Derived From</span>
                          <span className="text-text-primary font-medium">
                            {derivedFromLabel}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between gap-4">
                        <span className="text-text-secondary">Risk Profile</span>
                        <span className="text-text-primary font-medium">{resolvedRiskContext.profileName}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-text-secondary">Confirmed in Pre-Trading</span>
                        <span className="text-text-primary font-medium">{confirmedInPreTrading ? 'Yes' : 'No'}</span>
                      </div>
                      {showDividerSection && (
                        <div className="flex flex-col gap-1 pt-1 border-t border-default-200">
                          {isSessionOverride && (
                            <span className="text-xs text-text-tertiary">Differs from standard settings</span>
                          )}
                          {resolvedRiskContext.deviationNoteText && (
                            <p className="text-text-primary text-xs line-clamp-3">{resolvedRiskContext.deviationNoteText}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Execution panel ─────────────────────── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide whitespace-nowrap">
            Execution
          </span>
          <Divider />
        </div>

        {/* ── State 0: Draft (not yet promoted) ────────────────────────────────── */}
        {promotedTrade === null && (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-text-tertiary">
              Log your first order or fill to create a trade record.
            </p>
            {/* Promotion gate message — shown when required fields are missing */}
            {!isPromotionGateOpen && (
              <div className="rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 px-3 py-2 text-xs text-warning-700 dark:text-warning-300">
                {missingLabels.length > 0 && (
                  <p>
                    <span className="font-medium">Missing:</span>{' '}
                    {missingLabels.join(', ')}
                  </p>
                )}
                {isInstrumentLoading && (
                  <p className="mt-1">Loading instrument details…</p>
                )}
                {hasSetupTabBlockers && (
                  <button
                    type="button"
                    className="mt-1 text-xs font-medium text-warning-700 dark:text-warning-300 underline underline-offset-2 hover:text-warning-800 dark:hover:text-warning-200"
                    onClick={() => setActiveTab('setup')}
                  >
                    Go to Setup
                  </button>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="bordered"
                className="flex-1"
                isDisabled={!isPromotionGateOpen}
                onPress={() => handleOpenExecutionModal('order', undefined, undefined, undefined, addOrderLockedDirection)}
              >
                + Add Order
              </Button>
              <Button
                size="sm"
                variant="bordered"
                className="flex-1"
                isDisabled={!isPromotionGateOpen}
                onPress={() => handleOpenExecutionModal('fill')}
              >
                + Add Fill
              </Button>
            </div>
          </div>
        )}

        {/* ── State 1 / 2 / 3: Promoted ─────────────────────────────────────────
            Once promoted, the initial draft buttons are gone.
            Layout varies by executionStatus.                                    */}
        {promotedTrade !== null && (
          <div className="flex flex-col gap-3">

            {/* Empty — promoted but TradeExecution not loaded yet */}
            {tradeExecution === null && (
              <p className="text-xs text-text-tertiary">Loading execution data…</p>
            )}

            {tradeExecution !== null && (
              <>
                {/* ── State 2: Open / Partial-Exit — 4 action buttons ─────────── */}
                {(executionStatus === 'open' || executionStatus === 'partial-exit') && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="bordered"
                      onPress={() => handleOpenExecutionModal('fill', undefined, undefined, 'exit', positionDirection)}
                    >
                      Close
                    </Button>
                    <Button
                      size="sm"
                      variant="bordered"
                      onPress={() => handleOpenExecutionModal('fill', undefined, undefined, 'entry', positionDirection)}
                    >
                      Add to Position
                    </Button>
                    <Button
                      size="sm"
                      variant="bordered"
                      onPress={handleOpenSlModal}
                    >
                      {tradeExecution.stopLossCurrent !== undefined
                        ? `SL: ${tradeExecution.stopLossCurrent}`
                        : 'Place Stop Loss'}
                    </Button>
                    <Button
                      size="sm"
                      variant="bordered"
                      onPress={handleOpenTpModal}
                    >
                      {tradeExecution.takeProfitCurrent !== undefined
                        ? `TP: ${tradeExecution.takeProfitCurrent}`
                        : 'Place Profit Target'}
                    </Button>
                  </div>
                )}

                {/* Projection summary grid */}
                <div className="grid grid-cols-2 gap-2 bg-default-50 rounded-lg p-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-text-tertiary">Status</span>
                    <span className={`text-sm font-semibold ${STATUS_CLASSES[executionStatus ?? ''] ?? 'text-text-primary'}`}>
                      {STATUS_LABELS[executionStatus ?? ''] ?? executionStatus}
                    </span>
                  </div>
                  {tradeExecution.avgEntryPrice !== undefined && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-text-tertiary">Avg Entry</span>
                      <span className="text-sm font-semibold text-text-primary">
                        {tradeExecution.avgEntryPrice.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {tradeExecution.remainingQuantity !== undefined && executionStatus !== 'closed' && executionStatus !== 'cancelled' && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-text-tertiary">Remaining Qty</span>
                      <span className="text-sm font-semibold text-text-primary">
                        {tradeExecution.remainingQuantity}
                      </span>
                    </div>
                  )}
                  {tradeExecution.avgExitPrice !== undefined && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-text-tertiary">Avg Exit</span>
                      <span className="text-sm font-semibold text-text-primary">
                        {tradeExecution.avgExitPrice.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {tradeExecution.stopLossCurrent !== undefined && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-text-tertiary">Stop Loss</span>
                      <span className="text-sm font-semibold text-danger">
                        {tradeExecution.stopLossCurrent}
                      </span>
                    </div>
                  )}
                  {tradeExecution.takeProfitCurrent !== undefined && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-text-tertiary">Take Profit</span>
                      <span className="text-sm font-semibold text-success">
                        {tradeExecution.takeProfitCurrent}
                      </span>
                    </div>
                  )}
                </div>

                {/* ── Active order rows (pending / amended / partial-filled) ───── */}
                {(() => {
                  const activeRows = orderRows.filter(r => r.status !== 'filled');
                  if (activeRows.length === 0) return null;
                  const isTerminal = executionStatus === 'closed' || executionStatus === 'cancelled';
                  return (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-text-tertiary uppercase tracking-wide whitespace-nowrap">
                          Orders
                        </span>
                        <Divider />
                      </div>
                      <div className="flex flex-col gap-2">
                        {activeRows.map((row) => {
                          const { event: orderEvent, status, currentQuantity, currentPrice, filledQuantity, remainingQuantity } = row;
                          const isCancelledRow = status === 'cancelled';
                          const isPartial = filledQuantity > 0 && status !== 'filled';
                          return (
                            <div
                              key={orderEvent.id}
                              className={`flex flex-col gap-1.5 border rounded-lg p-2 ${isCancelledRow ? 'border-default-100 opacity-50' : 'border-default-200'}`}
                            >
                              {/* Row header */}
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                  {orderEvent.direction !== undefined && (
                                    <span className={`text-xs font-semibold ${orderEvent.direction === 'long' ? 'text-success' : 'text-danger'}`}>
                                      {orderEvent.direction === 'long' ? 'Long' : 'Short'}
                                    </span>
                                  )}
                                  {/* Show remaining qty if partially filled, else original qty */}
                                  {isPartial && remainingQuantity !== undefined ? (
                                    <span className="text-xs text-text-primary">
                                      {remainingQuantity}
                                      <span className="text-text-tertiary"> of {currentQuantity} remaining</span>
                                    </span>
                                  ) : currentQuantity !== undefined && (
                                    <span className="text-xs text-text-primary">{currentQuantity}</span>
                                  )}
                                  {currentPrice !== undefined && (
                                    <span className="text-xs text-text-secondary">@ {currentPrice}</span>
                                  )}
                                  {orderEvent.orderType !== undefined && (
                                    <span className="text-xs text-text-tertiary">({orderEvent.orderType})</span>
                                  )}
                                </div>
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${
                                  status === 'cancelled' ? 'bg-danger/10 text-danger' :
                                  status === 'amended' ? 'bg-warning/10 text-warning' :
                                  isPartial ? 'bg-primary/10 text-primary' :
                                  'bg-default-100 text-text-secondary'
                                }`}>
                                  {status === 'cancelled' ? 'Cancelled' :
                                   status === 'amended' ? 'Amended' :
                                   isPartial ? 'Partial' : 'Pending'}
                                </span>
                              </div>
                              <span className="text-[10px] text-text-tertiary">{fmtTime(orderEvent.occurredAt)}</span>
                              {/* Per-row actions — active rows in non-terminal states only */}
                              {!isCancelledRow && !isTerminal && (
                                <div className="flex gap-1.5 pt-0.5">
                                  <Button
                                    size="sm"
                                    variant="bordered"
                                    className="flex-1 text-xs min-w-0"
                                    onPress={() => {
                                      // F-07 (PR62): route opposite-direction order fills as exits.
                                      // Filling a short order against a long position (or vice versa)
                                      // reduces the position — must go through logClose(), not logFill().
                                      const isOppositeDir =
                                        positionDirection !== undefined &&
                                        orderEvent.direction !== undefined &&
                                        orderEvent.direction !== positionDirection;
                                      handleOpenExecutionModal(
                                        'fill',
                                        orderEvent,
                                        remainingQuantity,
                                        isOppositeDir ? 'exit' : 'entry',
                                      );
                                    }}
                                  >
                                    Filled
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="bordered"
                                    className="flex-1 text-xs min-w-0"
                                    onPress={() => handleOpenExecutionModal('amend', orderEvent)}
                                  >
                                    Amend
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="bordered"
                                    className="flex-1 text-xs min-w-0 text-danger"
                                    onPress={() => handleOpenExecutionModal('cancel', orderEvent)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}

                {/* ── Add Order button — non-terminal states ───────────────────── */}
                {executionStatus !== 'closed' && executionStatus !== 'cancelled' && (
                  <Button
                    size="sm"
                    variant="bordered"
                    className="w-full"
                    onPress={() => handleOpenExecutionModal('order', undefined, undefined, undefined, addOrderLockedDirection)}
                  >
                    + Add Order
                  </Button>
                )}

                {/* ── Event history ────────────────────────────────────────────── */}
                {sortedExecutionEvents.length > 0 && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-text-tertiary uppercase tracking-wide whitespace-nowrap">
                        History
                      </span>
                      <Divider />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {sortedExecutionEvents.map((event) => {
                        // T16: entry-fill linked to an order row → "Order Filled"
                        const label = (
                          event.eventType === 'entry-fill' && event.targetOrderEventId !== undefined
                            ? 'Order Filled'
                            : EVENT_LABELS[event.eventType] ?? event.eventType
                        );
                        return (
                          <div key={event.id} className="flex items-start justify-between gap-2 text-xs">
                            <span className="text-text-secondary font-medium whitespace-nowrap">
                              {label}
                            </span>
                            <div className="flex flex-col items-end gap-0.5 min-w-0">
                              {(event.direction !== undefined ||
                                event.quantity !== undefined ||
                                event.price !== undefined) && (
                                <span className="text-text-primary">
                                  {[
                                    event.direction,
                                    event.quantity !== undefined ? String(event.quantity) : undefined,
                                    event.price !== undefined ? `@ ${event.price}` : undefined,
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                </span>
                              )}
                              <span className="text-text-tertiary">{fmtTime(event.occurredAt)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // T09: QualitativeReviewSection — placeholder in persistent-trade mode only
  const reviewContent = promotedTrade !== null ? (
    <QualitativeReviewSection
      value={qualReview}
      onChange={setQualReview}
      draftId={draftId}
    />
  ) : null;

  // ---------------------------------------------------------------------------
  // Log as Missed inline expansion — shown in Setup tab body when open
  // ---------------------------------------------------------------------------

  const logAsMissedExpansion = (
    <div className="flex flex-col gap-3 border-t border-default-200 pt-3 mt-1">
      <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
        Why was this trade missed?
      </p>
      <Select
        label="Reason Category"
        size="sm"
        variant="bordered"
        selectedKeys={reasonCategoryId ? [reasonCategoryId] : []}
        onSelectionChange={(keys) => {
          if (keys === 'all') return;
          const key = Array.from(keys)[0] as string | undefined;
          handleReasonCategoryChange(key ?? '');
        }}
        popoverProps={drawerSelectPopoverProps}
        {...(!isDesktop ? { classNames: { value: 'text-base' } } : {})}
      >
        {MOCK_REASON_CATEGORIES.map((c) => (
          <SelectItem key={c.categoryId}>{c.name}</SelectItem>
        ))}
      </Select>
      <Select
        label="Reason"
        size="sm"
        variant="bordered"
        isDisabled={!reasonCategoryId}
        selectedKeys={reasonId ? [reasonId] : []}
        onSelectionChange={(keys) => {
          if (keys === 'all') return;
          const key = Array.from(keys)[0] as string | undefined;
          setReasonId(key ?? '');
        }}
        popoverProps={drawerSelectPopoverProps}
        {...(!isDesktop ? { classNames: { value: 'text-base' } } : {})}
      >
        {filteredReasons.map((r) => (
          <SelectItem key={r.snapshot.reasonId}>{r.snapshot.name}</SelectItem>
        ))}
      </Select>
    </div>
  );

  // ---------------------------------------------------------------------------
  // T11: Setup tab body — Strategy & Playbook, Criteria, Confluence, Entry Model only.
  // Notes/images moved to Docs tab; Execution moved to Execution tab; Review moved to Review tab.
  // ---------------------------------------------------------------------------

  const setupDesktopBody = (
    <Accordion
      selectionMode="single"
      defaultExpandedKeys={['strategy']}
      variant="light"
      className="px-0 w-full"
    >
      <AccordionItem key="strategy" aria-label="Strategy & Playbook" title="Strategy & Playbook">
        {strategyPlaybookContent}
      </AccordionItem>
      <AccordionItem key="criteria" aria-label="Criteria Evaluation" title="Criteria Evaluation">
        {criteriaContent}
      </AccordionItem>
      <AccordionItem key="confluence" aria-label="Confluence Factors" title="Confluence Factors">
        {confluenceContent}
      </AccordionItem>
      <AccordionItem key="entry-models" aria-label="Entry Model" title="Entry Model">
        {entryModelsContent}
      </AccordionItem>
    </Accordion>
  );

  const setupMobileBody = (
    <div className="flex flex-col gap-4 pb-2">
      <div>
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
          Strategy &amp; Playbook
        </p>
        {strategyPlaybookContent}
      </div>

      <Divider />

      <div>
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
          Criteria Evaluation
        </p>
        {criteriaContent}
      </div>

      <Divider />

      <div>
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
          Confluence Factors
        </p>
        {confluenceContent}
      </div>

      <Divider />

      <div>
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
          Entry Model
        </p>
        {entryModelsContent}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Footer CTAs — T10 contract
  // ---------------------------------------------------------------------------

  // Log as Missed expanded footer (shared for all modes when expansion is open)
  const footerLogAsMissedDesktop = (
    <div className="flex items-center justify-between w-full gap-2">
      <Button size="sm" variant="light" onPress={handleCancelLogAsMissed}>
        Cancel
      </Button>
      <Button
        size="sm"
        color="primary"
        variant="solid"
        isLoading={isCommitting}
        isDisabled={!reasonCategoryId || !reasonId || isCommitting}
        onPress={() => { void handleLogAsMissedConfirm(); }}
      >
        Confirm ✓
      </Button>
    </div>
  );

  const footerLogAsMissedMobile = (
    <div className="flex flex-col gap-2 w-full">
      <Button
        color="primary"
        variant="solid"
        isLoading={isCommitting}
        isDisabled={!reasonCategoryId || !reasonId || isCommitting}
        className="w-full"
        onPress={() => { void handleLogAsMissedConfirm(); }}
      >
        Confirm ✓
      </Button>
      <Button variant="light" onPress={handleCancelLogAsMissed} className="w-full">
        Cancel
      </Button>
    </div>
  );

  // New draft footer — mode === 'new', no promotion yet
  const footerNewDraftDesktop = (
    <div className="flex items-center justify-between w-full gap-2">
      <Button
        size="sm"
        variant="bordered"
        isDisabled={isCommitting}
        onPress={() => { void handleShelve(); }}
      >
        Shelve
      </Button>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="bordered"
          isDisabled={isCommitting}
          onPress={() => setIsLogAsMissedOpen(true)}
        >
          Log as Missed
        </Button>
        <Button
          size="sm"
          color="primary"
          variant="solid"
          isLoading={isCommitting}
          isDisabled={isCommitting}
          onPress={() => { void handleSaveDraft(); }}
        >
          Save Draft ✓
        </Button>
      </div>
    </div>
  );

  const footerNewDraftMobile = (
    <div className="flex flex-col gap-2 w-full">
      <Button
        color="primary"
        variant="solid"
        isLoading={isCommitting}
        isDisabled={isCommitting}
        className="w-full"
        onPress={() => { void handleSaveDraft(); }}
      >
        Save Draft ✓
      </Button>
      <Button
        variant="bordered"
        isDisabled={isCommitting}
        className="w-full"
        onPress={() => { void handleShelve(); }}
      >
        Shelve
      </Button>
      <Button
        variant="bordered"
        isDisabled={isCommitting}
        className="w-full"
        onPress={() => setIsLogAsMissedOpen(true)}
      >
        Log as Missed
      </Button>
    </div>
  );

  // Saved draft clean footer — edit mode, no changes
  const shelveLabel = existingDraft?.isShelved ? 'Unshelve' : 'Shelve';
  const handleShelveToggleDraft = existingDraft?.isShelved
    ? () => { void handleReactivate(); }
    : () => { void handleShelveEdit(); };

  const footerSavedDraftCleanDesktop = (
    <div className="flex items-center gap-2 justify-end w-full">
      <Button
        size="sm"
        variant="bordered"
        isDisabled={isCommitting}
        onPress={handleShelveToggleDraft}
      >
        {shelveLabel}
      </Button>
      <Button
        size="sm"
        variant="bordered"
        isDisabled={isCommitting}
        onPress={() => setIsLogAsMissedOpen(true)}
      >
        Log as Missed
      </Button>
    </div>
  );

  const footerSavedDraftCleanMobile = (
    <div className="flex flex-col gap-2 w-full">
      <Button
        variant="bordered"
        isDisabled={isCommitting}
        className="w-full"
        onPress={handleShelveToggleDraft}
      >
        {shelveLabel}
      </Button>
      <Button
        variant="bordered"
        isDisabled={isCommitting}
        className="w-full"
        onPress={() => setIsLogAsMissedOpen(true)}
      >
        Log as Missed
      </Button>
    </div>
  );

  // Saved draft dirty footer — edit mode with unsaved changes
  const footerSavedDraftDirtyDesktop = (
    <div className="flex items-center justify-between w-full gap-2">
      <Button
        size="sm"
        variant="light"
        isDisabled={isCommitting}
        onPress={handleDiscardChanges}
      >
        Discard Changes
      </Button>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="bordered"
          isDisabled={isCommitting}
          onPress={handleShelveToggleDraft}
        >
          {shelveLabel}
        </Button>
        <Button
          size="sm"
          variant="bordered"
          isDisabled={isCommitting}
          onPress={() => setIsLogAsMissedOpen(true)}
        >
          Log as Missed
        </Button>
        <Button
          size="sm"
          color="primary"
          variant="solid"
          isLoading={isCommitting}
          isDisabled={isCommitting}
          onPress={() => { void handleSaveChanges(); }}
        >
          Save Draft ✓
        </Button>
      </div>
    </div>
  );

  const footerSavedDraftDirtyMobile = (
    <div className="flex flex-col gap-2 w-full">
      <Button
        color="primary"
        variant="solid"
        isLoading={isCommitting}
        isDisabled={isCommitting}
        className="w-full"
        onPress={() => { void handleSaveChanges(); }}
      >
        Save Draft ✓
      </Button>
      <Button
        variant="bordered"
        isDisabled={isCommitting}
        className="w-full"
        onPress={handleShelveToggleDraft}
      >
        {shelveLabel}
      </Button>
      <Button
        variant="bordered"
        isDisabled={isCommitting}
        className="w-full"
        onPress={() => setIsLogAsMissedOpen(true)}
      >
        Log as Missed
      </Button>
      <Button
        variant="light"
        isDisabled={isCommitting}
        className="w-full"
        onPress={handleDiscardChanges}
      >
        Discard Changes
      </Button>
    </div>
  );

  // Persistent-trade footer — execution-status + workflowStatus driven
  const persistentShelveLabel = promotedTrade?.isShelved ? 'Unshelve' : 'Shelve';

  const footerPersistentTradeDesktop = (() => {
    if (promotedTrade?.workflowStatus === 'complete') {
      return (
        <div className="flex items-center w-full">
          <span className="text-xs text-default-400">Trade complete</span>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-between w-full gap-2">
        <Button
          size="sm"
          variant="bordered"
          isDisabled={isWorkflowUpdating}
          onPress={() => { void handleShelveTogglePersistent(); }}
        >
          {persistentShelveLabel}
        </Button>
        {canMarkComplete ? (
          <Button
            size="sm"
            color="primary"
            variant="solid"
            isLoading={isWorkflowUpdating}
            isDisabled={isWorkflowUpdating}
            onPress={() => { void handleComplete(); }}
          >
            Complete ✓
          </Button>
        ) : (
          <Button
            size="sm"
            variant="bordered"
            isLoading={isWorkflowUpdating && canMarkReady}
            isDisabled={!canMarkReady || isWorkflowUpdating}
            onPress={() => { void handleMarkReady(); }}
          >
            Mark Ready
          </Button>
        )}
      </div>
    );
  })();

  const footerPersistentTradeMobile = (() => {
    if (promotedTrade?.workflowStatus === 'complete') {
      return (
        <div className="flex items-center w-full justify-center">
          <span className="text-xs text-default-400">Trade complete</span>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2 w-full">
        {canMarkComplete ? (
          <Button
            color="primary"
            variant="solid"
            isLoading={isWorkflowUpdating}
            isDisabled={isWorkflowUpdating}
            className="w-full"
            onPress={() => { void handleComplete(); }}
          >
            Complete ✓
          </Button>
        ) : (
          <Button
            variant="bordered"
            isLoading={isWorkflowUpdating && canMarkReady}
            isDisabled={!canMarkReady || isWorkflowUpdating}
            className="w-full"
            onPress={() => { void handleMarkReady(); }}
          >
            Mark Ready
          </Button>
        )}
        <Button
          variant="bordered"
          isDisabled={isWorkflowUpdating}
          className="w-full"
          onPress={() => { void handleShelveTogglePersistent(); }}
        >
          {persistentShelveLabel}
        </Button>
      </div>
    );
  })();

  // Resolve active footer
  const isEditMode = mode === 'edit';

  const activeFooterDesktop = isLogAsMissedOpen && promotedTrade === null
    ? footerLogAsMissedDesktop
    : promotedTrade !== null
      ? footerPersistentTradeDesktop
      : isEditMode && isDirty
        ? footerSavedDraftDirtyDesktop
        : isEditMode
          ? footerSavedDraftCleanDesktop
          : footerNewDraftDesktop;

  const activeFooterMobile = isLogAsMissedOpen && promotedTrade === null
    ? footerLogAsMissedMobile
    : promotedTrade !== null
      ? footerPersistentTradeMobile
      : isEditMode && isDirty
        ? footerSavedDraftDirtyMobile
        : isEditMode
          ? footerSavedDraftCleanMobile
          : footerNewDraftMobile;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const drawerTitle = promotedTrade !== null
    ? 'Active Trade'
    : mode === 'edit'
      ? 'Edit Trade'
      : 'New Trade';

  return (
    <>
    <Drawer
      isOpen={isOpen}
      onOpenChange={(open) => { if (!open) onClose(); }}
      placement={isDesktop ? 'right' : 'bottom'}
      backdrop={isDesktop ? 'blur' : 'opaque'}
      size={isDesktop ? 'lg' : 'full'}
    >
      <DrawerContent>
        {/* Fixed header + tab navigation */}
        <DrawerHeader className="flex flex-col gap-0 p-0 border-b border-default-200">
          <div className="flex flex-col gap-1 px-4 pt-4 pb-2">
            <p className="text-sm font-semibold text-text-primary">{drawerTitle}</p>
            <Input
              label="Trade name"
              value={tradeName}
              onValueChange={handleTradeNameChange}
              placeholder="e.g. ICT:OTE Setup 09:30 (auto-filled)"
              size="sm"
              variant="underlined"
              classNames={{ inputWrapper: 'px-0', ...(!isDesktop ? { input: 'text-base' } : {}) }}
            />
          </div>
          {/* T10: Tab navigation row */}
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key as DrawerTab)}
            variant="underlined"
            size="sm"
            classNames={{
              base: 'w-full',
              tabList: 'w-full gap-0 px-2 h-9',
              tab: 'flex-1 text-xs',
              panel: 'hidden',
            }}
          >
            <Tab key="setup" title="Setup" />
            <Tab key="docs" title="Docs" />
            <Tab key="execution" title="Execution" />
            <Tab key="review" title="Review" />
          </Tabs>
        </DrawerHeader>

        {/* Scrollable body — tab-gated content */}
        <DrawerBody className="py-4 px-4 overflow-x-hidden">
          <div ref={setDrawerPopoverContainerRef}>
          {activeTab === 'setup' && (isDesktop ? setupDesktopBody : setupMobileBody)}
          {/* T11: Docs tab — NotesSection (T12 restructures to phase sections) */}
          {activeTab === 'docs' && notesContent}
          {/* T11: Execution tab — ExecutionSection draft (T13 rebuilds with InstrumentDropdown + state machine) */}
          {activeTab === 'execution' && executionContent}
          {/* T11: Review tab — QualitativeReviewSection (T15 restructures); null in draft mode */}
          {activeTab === 'review' && (
            reviewContent ?? (
              <div className="text-sm text-default-400 py-8 text-center">
                Review available on promoted trades
              </div>
            )
          )}
          {isLogAsMissedOpen && promotedTrade === null && logAsMissedExpansion}
          {promoteError !== null && (
            <div className="mt-3 rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger">
              {promoteError}
            </div>
          )}
          </div>
        </DrawerBody>

        {/* Fixed footer */}
        <DrawerFooter className="border-t border-default-200 pt-3">
          {isDesktop ? activeFooterDesktop : activeFooterMobile}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    {/* T14: Execution Event modal — portaled; outside drawer DOM */}
    <ExecutionEventModal
      isOpen={isExecutionModalOpen}
      onClose={() => setIsExecutionModalOpen(false)}
      mode={executionModalMode}
      tradeExecution={tradeExecution}
      {...(sessionDraftId !== undefined ? { sessionId: sessionDraftId } : {})}
      {...(promotedTrade === null ? { draftForPromotion: buildDraftForPromotion() } : {})}
      {...(promotedTrade === null && selectedInstrumentSnapshotRef.current !== undefined
        ? { instrumentSnapshot: selectedInstrumentSnapshotRef.current }
        : {})}
      {...(targetOrderEvent !== null ? { targetOrderEventId: targetOrderEvent.id } : {})}
      {...(fillPreseededDirection !== undefined ? { initialDirection: fillPreseededDirection } : {})}
      {...(fillPreseededQuantity !== undefined ? { initialQuantity: fillPreseededQuantity } : {})}
      {...(fillPreseededPrice !== undefined ? { initialPrice: fillPreseededPrice } : {})}
      {...(fillMaxQuantity !== undefined ? { maxQuantity: fillMaxQuantity } : {})}
      {...(fillInitialSubMode !== undefined ? { initialFillSubMode: fillInitialSubMode } : {})}
      {...((executionModalMode === 'fill' && fillPreseededDirection !== undefined) || modalLockDirection ? { lockDirection: true } : {})}
      {...(positionDirection !== undefined ? { positionDirection } : {})}
      {...(modalMaxOppositeQtyOverride !== undefined
        ? { maxOppositeOrderQty: modalMaxOppositeQtyOverride }
        : maxOppositeOrderQty !== undefined
          ? { maxOppositeOrderQty }
          : {})}
      {...(maxAmendQty !== undefined ? { maxAmendQty } : {})}
      {...(orderInitialType !== undefined ? { initialOrderType: orderInitialType } : {})}
      {...(orderLockType ? { lockOrderType: true } : {})}
      {...(modalRelationalOpponentPrices !== undefined ? { relationalOpponentPrices: modalRelationalOpponentPrices } : {})}
      {...(activeExitOrdersForAllocation.length > 0 ? { activeExitOrdersForAllocation } : {})}
      onSuccess={handleExecutionSuccess}
    />
    </>
  );
};
